import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { SchoolDashboardView } from './SchoolDashboardView'
import {
  AlertIcon,
  CheckboxRow,
  CheckIcon,
  ChildrenIcon,
  DashButton,
  DataSurface,
  Field,
  InfoIcon,
  Note,
  Panel,
  PlayIcon,
  PointIcon,
  PrivacyIcon,
  SchoolIcon,
  SectionHead,
  Select,
  StatCard,
  StatGrid,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Tr,
  TrendUpIcon,
  Input,
  PageDateFilterBar,
  Pagination,
  usePagination,
  PasswordInput,
  type StatusTone,
} from '../../../shared/components'
import { exportToCsv, exportToExcel } from '../../../shared/lib/exportData'
import { useResource, type Resource } from '../../../shared/hooks/useResource'
import { getPlatformGames, type PlatformGames } from '../../../shared/api/games'
import { GAME_TITLES } from '../../../shared/ui/gameTitles'
import type { GameId } from '../../../shared/curriculum'
import { tileValue } from './census'
import {
  changeSchoolRegion,
  createSchool,
  getPlatformActivity,
  getPlatformGrowth,
  getPlatformOverview,
  getSchoolDetail,
  getSchoolStaff,
  getSchools,
  provisionSchoolAdmin,
  resendAdminInvite,
  transitionSchool,
  /* The wire type and the component share a name; the read is aliased so the
     card below can refer to both. */
  type ActivityFeed as ActivityFeedData,
  type CreatedSchool,
  type GrowthSeries,
  type PlatformOverview,
  type ProvisionedAdmin,
  type SchoolDetail,
  type SchoolMetrics,
  type SchoolStaff,
  type SchoolSummary,
} from '../../../shared/api/platform'
/* ⚠️ F3's OWN CLIENT, IMPORTED RATHER THAN A SECOND PLAN TYPE DECLARED HERE.
   The onboarding wizard's third step sets a plan, which is the same catalogue
   the subscriptions screen renders — and `formatPaise` is the only thing in this
   product that turns paise into a price a school would recognise. */
import { getPlans, formatPaise, type PlanRow } from '../../../shared/api/billing'
import { ActivityFeed } from './ActivityFeed'
import { GrowthChart } from './GrowthChart'
import { RoleSplit } from './RoleSplit'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from '../../../shared/i18n'
import type { Region, SchoolStatus } from '../../../shared/types/prisma-enums'
import {
  PLATFORM_COPY,
  REGION_LABEL,
  REGIONS_BY_NAME,
  STATUS_LABEL,
  describePlatformError,
  healthVerdictText,
  passwordProblemText,
} from './copy'
import {
  classifyWriteError,
  isTerminal,
  needsConfirmation,
  summariseSchools,
  type SchoolFilter,
  OPERATOR_PASSWORD_MAX,
  orderSchoolAdmin,
  orderSchools,
  passwordState,
  reasonIsUsable,
  topGamesByPlays,
  topSchoolsByEngagement,
  type PasswordState,
  type WriteFailure,
  /* F2's onboarding flow. Every decision it makes is one of these — see the
     header on `CreateSchoolForm` for why none of them is in the component. */
  ONBOARDING_STEPS,
  SEAT_LIMIT_MAX,
  TRIAL_DAYS_MAX,
  TRIAL_DAYS_MIN,
  TRIAL_DAY_PRESETS,
  canResendInvite,
  emptyOnboardingDraft,
  nextStep,
  onboardingBody,
  onboardingOutcome,
  previousStep,
  stepBlockers,
  trialEndsOn,
  wantsAdmin,
  type OnboardingDraft,
  type OnboardingStep,
} from './platformModel'

/**
 * F1 and F2 — the platform operator's two screens.
 *
 * 🔴 THIS TIER SHIPPED ITS BACKEND AND NO UI AT ALL. `superadmin.controller.ts`,
 * `schools.service.ts`, `platform-metrics.ts` and `health-score.ts` were written,
 * tested and audited for a unit whose screens never existed — `#/platform`
 * rendered *"This tier has endpoints but no screen."* That is OD-89's failure
 * exactly ("four units of screens, mounted nowhere"), and the same discipline
 * fixes it: every screen below issues its own real request to its own real path,
 * and there is no fixture in this directory.
 *
 * 🔴 EVERY BRANCH WORTH TESTING IS IN `platformModel.ts`. The repo has no DOM
 * renderer (OD-60), so a decision made in a `.tsx` is a decision no test can
 * reach — and one of the buttons below archives a school irreversibly.
 *
 * ⚠️ **THE LAYOUT IS INVENTED.** `admin-panel.md` §2–§3 gives F1 a panel table
 * and F2 a lifecycle in prose; `wireframes.md` has no F-section at all. The
 * precedent for inventing a screen and saying so is OD-55. 📋 Reported.
 */

interface Base {
  lang?: Lang
  auth?: StaffAuth
}

/** The four states, wired to `DataSurface`'s contract in one place. */
function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  skeletonRows = 5,
}: {
  resource: Resource<T>
  lang: Lang
  children: (data: T) => ReactNode
  emptyMessage?: ReactNode
  skeletonRows?: number
}) {
  const t = PLATFORM_COPY[lang]
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.loadFailed}
      errorMessage={describePlatformError(resource.error, lang)}
      /* 🔴 "When it last worked" — the clause DataSurface says gets dropped,
         and on this tier the one that separates "the platform is down" from
         "I am not signed in as an operator". */
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  )
}


/**
 * What a write failure is called, in the operator's language.
 *
 * 🔴 `NAME_TAKEN` AND `EMAIL_TAKEN` ANSWER FOR THEMSELVES, AND THE `conflict`
 * ARGUMENT IS NOW ONLY THE FALLBACK.
 *
 * It used to be the whole answer for every 409, which is how the create form
 * told an operator *"a school with that name already exists"* when the thing
 * that clashed was the **admin's email address**. The school was fine, no
 * rename could help, and nothing was ever created — the form simply refused
 * every attempt with a sentence about the wrong field. The server now says
 * which, so the screen stops guessing.
 */
function failureText(failure: WriteFailure, lang: Lang, conflict: string): string {
  const t = PLATFORM_COPY[lang]
  switch (failure) {
    case 'NAME_TAKEN':
      return t.nameTaken
    case 'EMAIL_TAKEN':
      return t.emailTaken
    /* An unlabelled 409 — an older server, or a route with no code yet. The
       caller's guess is better than nothing here, and it is all that is left. */
    case 'CONFLICT':
      return conflict
    case 'REFUSED':
      return t.notAnOperator
    case 'INVALID':
      return t.transitionFailed
    default:
      return t.loadFailed
  }
}

/* ─────────────────────────────────────────────────────────────── F1 ── */

export interface PlatformDashboardProps extends Base {
  /** F2 lives at its own route; F1 links to it rather than restating it. */
  onOpenSchools?: () => void
  /**
   * The activity card's way through to the whole story.
   *
   * 🔴 A LINK RATHER THAN MORE ROWS ON THE CARD, AND THAT IS DELIBERATE. F5's
   * audit log is self-auditing — reading it writes a `SUPER_ADMIN_ACCESS` row —
   * so it is a screen an operator opens ON PURPOSE. A card that loaded it would
   * write one of those rows per dashboard view; a button that navigates to it
   * writes exactly one, when somebody meant to look.
   */
  onOpenAudit?: () => void
  /**
   * The enrolment tile's drill-down.
   *
   * 🔴 THE TILE HAS SAID "Children enrolled: 412" SINCE THIS SCREEN WAS BUILT
   * AND THERE WAS NOWHERE TO GO. Every question next to that number — is the
   * roster real, which of them have never played, has this particular child
   * played at all — ended at a total, because no surface in the product listed
   * children across schools.
   *
   * ⚠️ OPTIONAL, SO THE TILE IS STILL DRAWN WITHOUT IT — and without it the tile
   * is not a button. D4's rule: a control that cannot work is worse than no
   * control, because it tells an operator the product has a feature they are
   * being denied.
   */
  onOpenChildren?: () => void
  onOpenParents?: () => void
  /**
   * Change 19's engagement screen — where the ranked institutions card goes.
   *
   * ⚠️ OPTIONAL, AND WITHOUT IT THE "View All" IS NOT DRAWN AT ALL. Two of the
   * buttons in this card row used to be `<button>` elements with no handler,
   * which is D4's rule broken in the plainest possible way: a control that
   * cannot work tells an operator the product has a feature they are being
   * denied.
   */
  onOpenEngagement?: () => void
}

/**
 * F1 — *"the only screen with a time dimension; every lower tier is a snapshot"*.
 *
 * 🔴 THE AT-RISK LIST IS THE SCREEN, AND THE TOTALS ARE THE CONTEXT — not the
 * other way round. `admin-panel.md` §2: *"the number that predicts churn is
 * engagement, not MRR… surface at-risk schools here, not in a report nobody
 * opens."* So the schools needing a phone call sit above the fold, each carrying
 * the specific signal and the specific action, and the platform totals sit
 * beneath them.
 */
export function PlatformDashboard({
  lang = 'en',
  auth,
  onOpenSchools,
  onOpenAudit,
  onOpenChildren,
  onOpenParents,
  onOpenEngagement,
}: PlatformDashboardProps) {
  const t = PLATFORM_COPY[lang]

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split('T')[0]
  })

  /**
   * 🔴 THE PERIOD THE WHOLE SCREEN IS READ OVER, AND EVERY READ TAKES IT.
   *
   * It replaced a day COUNT derived from the same two dates and handed to the
   * growth route alone. That was wrong twice over: a count can only ever mean
   * *"the last N days"*, so picking a period that ended last month silently
   * redrew the chart over today — and the other two reads never saw the filter
   * at all, so the tiles above the chart answered a different question from the
   * chart itself while looking like one screen.
   *
   * ⚠️ NOT EVERY FIGURE ON THIS PAGE CAN MOVE, and the ones that cannot are not
   * being hidden. Seats, teacher headcount and the school roll are what EXISTS
   * today; `/superadmin/platform` explains why no date range can filter them
   * without inventing a history the tables do not record.
   */
  const period = useMemo(
    () => (fromDate === '' || toDate === '' ? undefined : { from: fromDate, to: toDate }),
    [fromDate, toDate],
  )
  /* One dependency string for all three reads, so they can never refetch out of
     step with each other and leave two periods on screen at once. */
  const periodKey = period === undefined ? 'default' : `${period.from}..${period.to}`

  const resource = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }, period),
    ['platform-overview', periodKey],
    { isEmpty: (o) => o.schools.total === 0 },
  )

  /*
   * ⚠️ `days` IS DELIBERATELY NOT SENT ALONGSIDE THE PERIOD. Its schema floors
   * at seven, so a five-day window would be refused with a 400 before the server
   * ever looked at `from`/`to` — a filter that works until somebody picks a short
   * week. The window supersedes it server-side anyway.
   */
  const growthResource = useResource<GrowthSeries>(
    (signal) => getPlatformGrowth(undefined, { ...auth, signal }, period),
    ['platform-growth', periodKey],
  )

  /*
   * ⚠️ THE PER-GAME READ THE FIFTH TILE NEEDED. It printed an em dash headed
   * "Games Played (This Month)"; `/superadmin/games` has answered exactly this
   * since F1, and carries its own window so the label can name the period.
   */
  const gamesResource = useResource<PlatformGames>(
    (signal) => getPlatformGames({ ...auth, signal }, period),
    ['platform-games', periodKey],
  )

  const latestGrowth = growthResource.data?.points.at(-1) ?? null

  /**
   * 🔴 THE DATE THE THREE HEADLINE TOTALS ARE TRUE ON, AND IT IS READ OFF THE
   * SERIES rather than off `toDate`. The curve is cumulative, so its last point
   * is "everything that existed by this day" — which is exactly what those tiles
   * show, and which stops being today the moment an operator picks a period that
   * ended in the past.
   *
   * ⚠️ TAKEN FROM THE DATA AND NOT FROM THE PICKER ON PURPOSE. While the growth
   * read is still in flight the tiles fall back to live counts, and a label
   * sourced from the picker would date those to a day nobody measured them on.
   * `null` here means "no series yet", and the labels below say nothing at all
   * rather than something wrong.
   */
  const asOf = useMemo(() => {
    const day = latestGrowth?.day
    if (day === undefined) return null
    const at = new Date(`${day}T00:00:00.000Z`)
    if (Number.isNaN(at.getTime())) return null
    return at.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
  }, [latestGrowth])
  const totalStudents = latestGrowth?.students ?? resource.data?.enrolment.seats ?? 0
  const totalTeachers = latestGrowth?.teachers ?? resource.data?.teachers.active ?? 0
  const totalParents = latestGrowth?.parents ?? 0
  const totalUsers = totalStudents + totalTeachers + totalParents

  /*
   * 🔴 STILL READ, BUT ONLY BY THE EXPORT NOW. The table this ordered was the
   * Schools screen's table a second time, and F2 answers the question better:
   * search, a status filter that filters, thirteen columns and a way into each
   * school. What the download must keep is the order an operator expects —
   * worst first, because a spreadsheet sorted by name buries the school about
   * to churn at the letter S.
   */
  const rows = useMemo(
    () => orderSchools(resource.data?.perSchool ?? [], 'risk'),
    [resource.data],
  )

  /** The schools whose children are actually playing — the ranked card below. */
  const topSchools = useMemo(
    () => topSchoolsByEngagement(resource.data?.perSchool ?? []),
    [resource.data],
  )

  /** The most-played games, ranked by rounds finished in the same window. */
  const topGames = useMemo(
    () => topGamesByPlays(gamesResource.data?.games ?? []),
    [gamesResource.data],
  )

  /*
   * ⚠️ `/superadmin/activity` IS NOT THE AUDIT LOG AND DOES NOT SELF-AUDIT.
   * Reading F5 writes a `SUPER_ADMIN_ACCESS` row, which is exactly why
   * `onOpenAudit` is a navigate rather than a fetch — see the prop's own note.
   * This route reads onboardings, plan changes, coupons and demo requests, names
   * nobody, and costs the log nothing, so the card is allowed to load itself.
   */
  const activityResource = useResource<ActivityFeedData>(
    (signal) => getPlatformActivity(6, { ...auth, signal }, period),
    ['platform-activity', periodKey],
  )

  const handleOverviewExportExcel = () => {
    const data = rows.map((s, idx) => ({
      '#': idx + 1,
      'Institute ID': s.schoolId,
      'School Name': s.name,
      'Region': s.region,
      'Enrolled Students': s.enrolledStudents,
      'Active Students': s.activeStudents,
      'Active Teachers': s.teachers,
      'Status': s.status,
      'Health Verdict': s.health.verdict,
    }))
    exportToExcel('Platform_Overview_Report', data, {
      /* 🔴 `?? 124` WROTE AN INVENTED TOTAL INTO A DOWNLOADED FILE. The fallback
         fires exactly when the overview has not loaded — so a report exported a
         moment too early left the building carrying a number nobody measured,
         with a filename and a date on it. Zero is what "not read" is worth. */
      totalInstitutions: resource.data?.schools.total ?? 0,
      totalIssues: resource.data?.atRisk.length ?? 0,
      totalStudents: totalStudents,
      totalTeachers: totalTeachers,
      totalParents: totalParents,
    })
  }

  const handleOverviewExportCsv = () => {
    const data = rows.map((s, idx) => ({
      '#': idx + 1,
      'Institute ID': s.schoolId,
      'School Name': s.name,
      'Region': s.region,
      'Enrolled Students': s.enrolledStudents,
      'Active Students': s.activeStudents,
      'Active Teachers': s.teachers,
      'Status': s.status,
      'Health Verdict': s.health.verdict,
    }))
    exportToCsv('Platform_Overview_Report', data, {
      /* `?? 0` for the reason the Excel export above states. */
      totalInstitutions: resource.data?.schools.total ?? 0,
      totalIssues: resource.data?.atRisk.length ?? 0,
      totalStudents: totalStudents,
      totalTeachers: totalTeachers,
      totalParents: totalParents,
    })
  }

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/*
        Top header — the heading on the left, the period controls on the right of
        the same band.

        ⚠️ `items-start` RATHER THAN `items-center`: the heading is two lines and
        the controls are one, so centring them would hang the row off the middle of
        a sentence. `flex-wrap` lets the controls drop below the heading intact on a
        narrow viewport, where `ml-auto` inside the bar keeps them right-aligned.
      */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-dash text-dash-2xl font-extrabold text-ink flex items-center gap-2">
            Welcome back, Super Admin!
          </h1>
          <p className="mt-1 font-dash text-dash-sm text-muted">
            Here's what's happening across the Kalqy platform.
          </p>
        </div>

        <PageDateFilterBar
          variant="inline"
          presetRanges={false}
          fromDate={fromDate}
          toDate={toDate}
          onApply={(from, to) => {
            setFromDate(from)
            setToDate(to)
          }}
          onClear={() => {
            const d = new Date()
            const toStr = d.toISOString().split('T')[0]
            d.setDate(d.getDate() - 30)
            const fromStr = d.toISOString().split('T')[0]
            setFromDate(fromStr)
            setToDate(toStr)
          }}
          onExportExcel={handleOverviewExportExcel}
          onExportCsv={handleOverviewExportCsv}
        />
      </div>

      <DataSurface
        state={resource.state}
        skeletonRows={6}
        emptyMessage={t.atRiskNone}
        emptyActionLabel={t.addSchool}
        onEmptyAction={onOpenSchools}
        errorMessage={describePlatformError(resource.error, lang)}
        errorLastWorked={
          resource.lastLoadedAt
            ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
            : t.neverWorked
        }
        errorRetryLabel={t.retry}
        onRetry={resource.retry}
      >
        {resource.data === null ? null : (
          <>
            {/* Top Health / At Risk Alert Banner if any */}
            {resource.data.atRisk.length > 0 && (
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <h2 className="text-dash-md font-extrabold text-ink">{t.atRiskTitle}</h2>
                  <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-dash-xs font-extrabold text-rose-700">
                    {resource.data.atRisk.length}
                  </span>
                </div>
                <ul className="flex flex-col gap-3">
                  {resource.data.atRisk.map((school) => (
                    <li key={school.schoolId}>
                      <Panel>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-dash-md font-extrabold text-ink">
                            {school.name}
                          </span>
                          <HealthPill health={school.health} lang={lang} />
                          <span className="text-dash-xs text-muted">
                            {STATUS_LABEL[school.status][lang]} {'·'} {REGION_LABEL[school.region]}
                          </span>
                        </div>
                      </Panel>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              🔴 THREE OF THESE TILES CARRIED A FICTION THAT ONLY APPEARED WHEN
              THE PLATFORM WAS EMPTY. They read `totalUsers > 0 ? totalUsers :
              '18,765'` — and 14,326 students, and 2,143 teachers. On a
              populated database the real figure won and the fallback was
              invisible; on a fresh deployment, a demo instance, or the morning
              a read failed, the operator dashboard announced eighteen thousand
              users. That is the worst possible direction for a fallback to
              fail in: it is silent exactly when it is wrong.

              ⚠️ ZERO IS NOW WHAT ZERO SHOWS. `resource.data` is non-null here —
              the surface above only renders this branch when the read
              succeeded — so a zero is a measurement, and 'no users yet' is a
              thing an operator of a new platform needs to be told.
            */}
            {/* 1. Top Stat Cards Grid (6 Cards) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* Card 1: Total Institutions */}
              <div
                className={`rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between ${
                  onOpenSchools ? 'cursor-pointer hover:border-primary transition-colors' : ''
                }`}
                onClick={onOpenSchools}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                    <SchoolIcon size={20} />
                  </div>
                </div>
                <div>
                  <span className="text-dash-xs font-bold text-muted block">Total Institutions</span>
                  <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                    {resource.data.schools.total}
                  </span>
                  <span className="text-dash-xs font-medium text-muted">
                    {`${String(resource.data.schools.byStatus.ACTIVE ?? 0)} active`}
                  </span>
                </div>
              </div>

              {/* Card 2: Total Users */}
              <div
                className={`rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between ${
                  onOpenChildren ? 'cursor-pointer hover:border-primary transition-colors' : ''
                }`}
                onClick={onOpenChildren}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <ChildrenIcon size={20} />
                  </div>
                </div>
                <div>
                  <span className="text-dash-xs font-bold text-muted block">Total Users</span>
                  <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                    {totalUsers.toLocaleString()}
                  </span>
                  <span className="text-dash-xs font-medium text-muted">
                    {asOf === null ? 'Students, teachers and parents' : `All roles, as of ${asOf}`}
                  </span>
                </div>
              </div>

              {/* Card 3: Total Students */}
              <div
                className={`rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between ${
                  onOpenChildren ? 'cursor-pointer hover:border-primary transition-colors' : ''
                }`}
                onClick={onOpenChildren}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <span className="text-dash-xs font-bold text-muted block">Total Students</span>
                  <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                    {totalStudents.toLocaleString()}
                  </span>
                  <span className="text-dash-xs font-medium text-muted">
                    {asOf === null ? 'Children holding a seat' : `Enrolled, as of ${asOf}`}
                  </span>
                </div>
              </div>

              {/* Card 4: Total Teachers */}
              <div
                className={`rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between ${
                  (onOpenParents || onOpenSchools) ? 'cursor-pointer hover:border-primary transition-colors' : ''
                }`}
                onClick={onOpenParents ?? onOpenSchools}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                    <PointIcon size={20} />
                  </div>
                </div>
                <div>
                  <span className="text-dash-xs font-bold text-muted block">Total Teachers</span>
                  <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                    {totalTeachers.toLocaleString()}
                  </span>
                  <span className="text-dash-xs font-medium text-muted">
                    {asOf === null ? 'Active teacher accounts' : `Teacher accounts, as of ${asOf}`}
                  </span>
                </div>
              </div>

              {/* Card 5: Games Played */}
              <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
                    <PlayIcon size={20} />
                  </div>
                </div>
                <div>
                  {/*
                    ⚠️ RELABELLED TO THE WINDOW THE SERVER ACTUALLY RETURNS. It
                    said "This Month" over an em dash; `/superadmin/games`
                    answers over its own window and carries `windowDays` with
                    the rows precisely so a screen cannot print the number
                    without the period that makes it true.
                  */}
                  <span className="text-dash-xs font-bold text-muted block">
                    {gamesResource.data === null
                      ? "Rounds finished"
                      : `Rounds finished (${String(gamesResource.data.windowDays)}d)`}
                  </span>
                  <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                    {gamesResource.data === null ? "—" : gamesResource.data.summary.rounds.toLocaleString()}
                  </span>
                  <span className="text-dash-xs font-medium text-muted">
                    {gamesResource.data === null
                      ? "Loading…"
                      : `${String(gamesResource.data.summary.gamesPlayed)} of ${String(gamesResource.data.summary.gamesAvailable)} games started`}
                  </span>
                </div>
              </div>

              {/* Card 6: Active Sessions */}
              <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-10 w-10 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center shrink-0">
                    <TrendUpIcon size={20} />
                  </div>
                </div>
                <div>
                  {/*
                    🔴 "ACTIVE SESSIONS (TODAY)" WAS A QUESTION NOTHING ANSWERS.
                    No endpoint counts sittings for a calendar day, and inventing
                    one here would have been the fourth fabricated tile in this
                    row. What IS measured is children who played inside the
                    overview’s own window, and the label now says so.
                  */}
                  <span className="text-dash-xs font-bold text-muted block">
                    {`Children who played (${String(resource.data.engagement.windowDays)}d)`}
                  </span>
                  <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
                    {resource.data.engagement.activeStudents.toLocaleString()}
                  </span>
                  <span className="text-dash-xs font-medium text-muted">
                    {`of ${String(resource.data.enrolment.seats)} enrolled`}
                  </span>
                </div>
              </div>
            </div>

            {/* 2. Middle Section (3 Columns Grid) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Card 1: User Growth Overview (Line Chart) */}
              <div className="lg:col-span-5 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-dash text-dash-md font-extrabold text-ink">User Growth Overview</h3>
                  {/* 🔴 "View Report" WAS A `<button>` WITH NO `onClick`. There is no
                      report screen for it to open, and D4's rule is that a control
                      which cannot work is worse than no control. What the header can
                      honestly carry instead is the period the line is drawn over. */}
                  <span className="text-dash-xs font-bold text-muted">
                    {period === undefined ? 'Last 30 days' : `${period.from} to ${period.to}`}
                  </span>
                </div>

                <Surface resource={growthResource} lang={lang} skeletonRows={4}>
                  {(growth) => <GrowthChart series={growth} />}
                </Surface>
              </div>

              {/* Card 2: Users by Role */}
              <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-dash text-dash-md font-extrabold text-ink">Users by Role</h3>
                  {/*
                    ⚠️ IT SAID "All time", WHICH WAS TRUE ONLY WHILE THE SERIES
                    ALWAYS ENDED TODAY. These slices are the growth curve's last
                    point; once the filter can end in the past, "all time" is a
                    label promising more than the number under it delivers.
                  */}
                  <span className="text-dash-xs font-bold text-muted">
                    {asOf === null ? 'All time' : `As of ${asOf}`}
                  </span>
                </div>

                <div className="mt-1">
                  {/*
                    🔴 THE SLICES ARE THE GROWTH SERIES', NOT THREE TYPED
                    NUMBERS. This read `count: 14326` for students beside a
                    `totalUsers` that was computed from the live growth points —
                    a chart whose parts were fiction and whose whole was real,
                    which is the shape that survives review longest. The invented
                    floor of 18,765 under the total is gone with them: a platform
                    with nobody on it must be allowed to say so.
                  */}
                  <RoleSplit
                    total={totalUsers}
                    roles={[
                      { key: 'students', label: 'Students', count: totalStudents, tint: 'bg-emerald-500' },
                      { key: 'teachers', label: 'Teachers', count: totalTeachers, tint: 'bg-blue-500' },
                      { key: 'parents', label: 'Parents', count: totalParents, tint: 'bg-purple-500' },
                    ]}
                  />
                </div>
              </div>

              {/* Card 3: Recent Activities */}
              <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-dash text-dash-md font-extrabold text-ink">Recent Activities</h3>
                  <button
                    type="button"
                    onClick={onOpenAudit}
                    className="text-dash-xs font-bold text-primary hover:underline cursor-pointer"
                  >
                    View All
                  </button>
                </div>

                <div className="flex flex-col gap-3 my-auto">
                  {/*
                    🔴 FIVE HAND-WRITTEN EVENTS, NOT A LIST. This card carried
                    "Payment received from 'Sunrise Public School'" and four
                    siblings as literal markup — not an array anyone could have
                    mistaken for data, just five paragraphs that never changed.
                    `ActivityFeed` renders the real thing from
                    `GET /superadmin/activity`; the View All button above still
                    opens the audit log, which is the whole story rather than the
                    six most recent lines of it.

                    ⚠️ NO `onOpenSchool` IS PASSED, so an event renders as plain
                    text rather than a link. This screen can navigate to the
                    schools LIST but not to one named school, and a button that
                    quietly opens the wrong place is worse than a sentence.
                  */}
                  <Surface resource={activityResource} lang={lang} skeletonRows={4}>
                    {(feed) => <ActivityFeed events={feed.events} />}
                  </Surface>
                </div>
              </div>
            </div>

            {/* 3. Lower Middle Section (2 Cards Grid) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Card 1: Top Institutions by Engagement */}
              <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-dash text-dash-md font-extrabold text-ink">Top Institutions by Engagement</h3>
                  {(onOpenEngagement ?? onOpenSchools) === undefined ? null : (
                    <button
                      type="button"
                      className="text-dash-xs font-bold text-primary hover:underline cursor-pointer"
                      onClick={onOpenEngagement ?? onOpenSchools}
                    >
                      View All
                    </button>
                  )}
                </div>

                <div className="space-y-3.5 my-auto">
                  {/*
                    🔴 FIVE INVENTED SCHOOLS RANKED BY AN INVENTED ENGAGEMENT
                    RATE — "Greenwood High, 94%" and four siblings, on a platform
                    whose real schools are none of them.

                    ⚠️ THE PAIR IS PRINTED UNDER EVERY RATE, and that is not
                    decoration: OD-39/OD-72 — *"30% is a grade whose meaning
                    depends entirely on what it was divided by"*. A school with
                    no seats at all is absent rather than shown at 0%, because
                    0/0 is an absence and this is a ranking of what IS happening.
                  */}
                  {topSchools.length === 0 ? (
                    <p className="font-dash text-dash-xs font-semibold text-muted">
                      No school has enrolled a child yet, so there is nothing to rank.
                    </p>
                  ) : (
                    topSchools.map((school, idx) => (
                      <div key={school.schoolId} className="flex items-center gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-[11px] font-extrabold text-muted tabular">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-dash-xs font-bold text-ink">{school.name}</span>
                            <span className="shrink-0 text-dash-xs font-extrabold text-ink tabular">
                              {`${String(school.ratePct)}%`}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-canvas">
                            <div
                              className="h-1.5 rounded-full bg-emerald-500"
                              style={{ width: `${String(school.ratePct)}%` }}
                            />
                          </div>
                          <span className="mt-0.5 block text-[11px] font-semibold text-muted tabular">
                            {`${String(school.activeStudents)} of ${String(school.enrolledStudents)} children played`}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Card 2: Top Games by Plays */}
              <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-dash text-dash-md font-extrabold text-ink">Top Games by Plays</h3>
                  {/* 🔴 THE SECOND HANDLER-LESS "View All". There is no platform game
                      screen to open, so the header carries the one thing these counts
                      cannot be read without: the window they were counted over. */}
                  <span className="text-dash-xs font-bold text-muted">
                    {gamesResource.data === null
                      ? ''
                      : `Last ${String(gamesResource.data.windowDays)} days`}
                  </span>
                </div>

                <div className="space-y-3 my-auto">
                  {/*
                    🔴 FIVE INVENTED GAMES WITH INVENTED PLAY COUNTS, in a product
                    that ships exactly five games and knows precisely how often
                    each was played. `GAME_TITLES` names them — the slug is the
                    fallback, never the label.

                    ⚠️ A GAME NOBODY HAS PLAYED IS NOT LISTED AT ZERO. "Top games
                    by plays" asserts that what it lists is among the most played;
                    `topGamesByPlays` drops the rest, and the tile above already
                    reports how many of the five have been opened at all.
                  */}
                  {gamesResource.data === null ? (
                    <p className="font-dash text-dash-xs font-semibold text-muted">
                      Game stats have not loaded.
                    </p>
                  ) : topGames.length === 0 ? (
                    <p className="font-dash text-dash-xs font-semibold text-muted">
                      {`Nobody has finished a round in the last ${String(gamesResource.data.windowDays)} days.`}
                    </p>
                  ) : (
                    topGames.map((game, idx) => (
                      <div key={game.game} className="flex items-center gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-[11px] font-extrabold text-muted tabular">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-dash-xs font-bold text-ink">
                              {GAME_TITLES[game.game as GameId]?.[lang] ?? game.game}
                            </span>
                            <span className="shrink-0 text-dash-xs font-extrabold text-ink tabular">
                              {game.rounds.toLocaleString()}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 w-full rounded-full bg-canvas">
                            <div
                              className="h-1.5 rounded-full bg-pink-500"
                              style={{ width: `${String(game.sharePct)}%` }}
                            />
                          </div>
                          <span className="mt-0.5 block text-[11px] font-semibold text-muted tabular">
                            {`${String(game.children)} ${game.children === 1 ? 'child' : 'children'} · ${String(game.sharePct)}% of all rounds`}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/*
              🔴 "EVERY SCHOOL" WAS THE SCHOOLS SCREEN'S TABLE, SHOWN AGAIN AND
              SHOWN WORSE. Both read `perSchool` off the same `/superadmin/platform`
              response, but F2 has search, a status filter that actually filters,
              thirteen columns and a click through into each school. This copy had
              seven columns and a control LABELLED "Status" that SORTED rather than
              filtered — so an operator who picked "Needs a call" got every school
              back, merely reordered, with nothing to tell them that nothing had
              been excluded. Two tables, one source, and only one of them honest
              about what it was showing.

              ⚠️ NOTHING WENT WITH IT. The schools that need a phone call are at
              the top of this screen in the at-risk list, ranked engagement is in
              the card above, the whole roster is one sidebar row away under
              Schools, and the export buttons in the filter bar still ship every
              row — worst first, which is why `rows` is still computed.
            */}
          </>
        )}
      </DataSurface>
    </section>
  )
}


function HealthPill({
  health,
  lang,
}: {
  health: SchoolMetrics['health']
  lang: Lang
}) {
  const tone: StatusTone =
    health.verdict === 'AT_RISK' ? 'danger' : health.verdict === 'WATCH' ? 'warning' : 'success'
  return (
    <StatusPill
      tone={tone}
      icon={health.verdict === 'OK' ? <CheckIcon size={14} /> : <AlertIcon size={14} />}
    >
      {healthVerdictText(health.verdict, lang)}
    </StatusPill>
  )
}

/* ─────────────────────────────────────────────────────────────── F2 ── */

export interface SchoolsScreenProps extends Base {
  /**
   * X4 — begin acting as a member of this school's staff.
   *
   * 🔴 A CALLBACK RATHER THAN A CALL THIS SCREEN MAKES ITSELF, because starting
   * a session REPLACES THE APP'S CREDENTIAL. Every other write on this tier
   * changes a row; this one changes who the browser is, which means the router
   * has to re-render into a different tier and the banner has to appear. A panel
   * that swapped the session store from inside a table cell would leave the
   * router showing the platform sidebar to somebody who is now a teacher.
   *
   * ⚠️ OPTIONAL, SO THE SCREEN STILL RENDERS WITHOUT IT — and when it is absent
   * the panel is **not drawn at all** rather than drawn disabled. D4's rule: a
   * control that cannot work is worse than no control, because it tells an
   * operator the product has a feature they are being denied.
   */
  onActAs?: (userId: string, reason: string) => Promise<void>
}

/**
 * F2 — onboarding, the lifecycle, and the first admin.
 *
 * 🔴 THIS SCREEN IS WHAT UNIT 10's GATE ASKS FOR: *"onboard a second school
 * without touching the database."* Every other route in this tier is a read or a
 * switch; this is the one that turns a sale into a working account.
 */
/**
 * One row of the institutions table.
 *
 * ⚠️ THE NULLABLE FIELDS ARE THE HONEST ONES. Each is a column the design asked
 * for and the platform has no answer to; typing them `| null` is what stops the
 * next edit quietly filling one in with a plausible number.
 */
interface SchoolsTableRow {
  id: string
  name: string
  domain: string | null
  plan: string | null
  loc: string
  classes: number
  admins: number
  students: number | null
  teachers: number | null
  active7d: number | null
  rate: number | null
  sub: string | null
  renewal: string | null
  status: SchoolStatus
}

/** What a column the platform cannot answer prints. Never a zero. */
const DASH = '—'

export function SchoolsScreen({ lang = 'en', auth, onActAs }: SchoolsScreenProps) {
  const t = PLATFORM_COPY[lang]
  const [openId, setOpenId] = useState<string | null>(null)
  const [selectedSchool, setSelectedSchool] = useState<{ id: string; name: string } | null>(null)

  const resource = useResource<SchoolSummary[]>(
    (signal) => getSchools({ ...auth, signal }),
    ['platform-schools'],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<SchoolFilter>('ALL')
  const [showAddModal, setShowAddModal] = useState(false)

  /*
   * 🔴 THESE TWO SAT **BELOW** THE `selectedSchool` EARLY RETURN, AND THAT WAS A
   * CRASH RATHER THAN AN UNTIDINESS.
   *
   * React identifies a hook by its call ORDER, so the count may not change
   * between renders. With these declared after `if (selectedSchool !== null)
   * return …`, the render that opened a school ran two fewer hooks than the one
   * before it — *"Rendered fewer hooks than expected"*, thrown from React, which
   * on this tier means clicking an institution's name blanked the screen.
   *
   * ⚠️ NOTHING CAUGHT IT: `eslint.config.mjs` does not load
   * `eslint-plugin-react-hooks`, so `rules-of-hooks` — the one rule written for
   * exactly this — was not running over this file.
   */
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])
  /* The page of institutions on screen, and how many fit on one. */
  const [pageSize, setPageSize] = useState(10)

  /*
   * 🔴 F1's OVERVIEW, READ HERE TOO, BECAUSE THE SCHOOL LIST DOES NOT CARRY
   * SEATS. `SchoolSummary` has a class count and an admin count and no children
   * or teachers at all, so the 'Total Students' and 'Total Teachers' tiles on
   * this screen — and the per-school Students / Teachers / Active columns in
   * the table — had nothing to read and printed em dashes. `perSchool` on the
   * platform overview has carried every one of them since F1 shipped.
   */
  const overviewResource = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }),
    ['platform-overview'],
  )

  /** Per-school metrics by id, so a table row can find its own figures. */
  const metricsById = useMemo(() => {
    const map = new Map<string, SchoolMetrics>()
    for (const m of overviewResource.data?.perSchool ?? []) map.set(m.schoolId, m)
    return map
  }, [overviewResource.data])

  const ordered = useMemo(() => orderSchoolAdmin(resource.data ?? []), [resource.data])
  const totals = useMemo(() => summariseSchools(ordered), [ordered])

  const open = ordered.find((s) => s.id === openId) ?? null

  if (selectedSchool !== null) {
    return (
      <SchoolDashboardView
        schoolId={selectedSchool.id}
        schoolName={selectedSchool.name}
        lang={lang}
        auth={auth}
        onBack={() => setSelectedSchool(null)}
        onActAs={onActAs}
      />
    )
  }

  /*
   * 🔴 THE REAL SCHOOLS, WHICH THIS SCREEN WAS ALREADY FETCHING AND THEN NOT
   * SHOWING. `resource` reads `GET /superadmin/schools` and `ordered` sorts it;
   * the table underneath rendered eight invented institutions instead —
   * "ABC International School", a Premium plan, 1,250 students, a renewal date —
   * and the CSV export shipped the same fiction out of the building.
   *
   * ⚠️ THE COLUMNS THE PLATFORM CANNOT ANSWER ARE `null`, NOT ZERO OR A GUESS.
   * There is no billing plan, no e-mail domain, no per-school seven-day active
   * count and no renewal date anywhere in `SchoolSummary` — those columns render
   * an em dash. A zero would read as a measured nothing.
   */
  const schoolsTableData: SchoolsTableRow[] = useMemo(
    () =>
      ordered.map((s) => ({
        id: s.id,
        name: s.name,
        domain: null,
        plan: null,
        loc: s.region,
        classes: s.classCount,
        admins: s.adminCount,
        /* ⚠️ STILL `null` WHEN THE OVERVIEW HAS NOT ARRIVED — an em dash means
           'not read', and must never become a zero that reads as measured. */
        students: metricsById.get(s.id)?.enrolledStudents ?? null,
        teachers: metricsById.get(s.id)?.teachers ?? null,
        active7d: metricsById.get(s.id)?.activeStudents ?? null,
        rate: metricsById.get(s.id)?.engagement ?? null,
        sub: null,
        renewal: null,
        status: s.status,
      })),
    [ordered, metricsById],
  )

  /*
   * ✅ THE ROWS THE TABLE ACTUALLY SHOWS, AND THE COUNT THAT GOES WITH THEM.
   * The status filter was applied inline inside the `.map`, so nothing else on
   * the screen could know how many rows survived it — which is how the heading
   * came to read a hardcoded 124 beside seven institutions.
   */
  const visibleSchools = useMemo(
    () => schoolsTableData.filter((s) => (filter === 'ALL' ? true : s.status === filter)),
    [schoolsTableData, filter],
  )

  const {
    page: schoolPage,
    totalPages: schoolTotalPages,
    setPage: setSchoolPage,
    paginatedItems: pagedSchools,
  } = usePagination(visibleSchools, pageSize)

  const handleSchoolsExportExcel = () => {
    const data = schoolsTableData.map((s, idx) => ({
      '#': idx + 1,
      'Institute ID': s.id,
      'Institution Name': s.name,
      'Region': s.loc,
      'Classes': s.classes,
      'Admins': s.admins,
      'Status': s.status,
    }))
    exportToExcel('Institutions_Master_List', data, {
      totalInstitutions: ordered.length,
      totalIssues: totals.noAdmin,
    })
  }

  const handleSchoolsExportCsv = () => {
    const data = schoolsTableData.map((s, idx) => ({
      '#': idx + 1,
      'Institute ID': s.id,
      'Institution Name': s.name,
      'Region': s.loc,
      'Classes': s.classes,
      'Admins': s.admins,
      'Status': s.status,
    }))
    exportToCsv('Institutions_Master_List', data, {
      totalInstitutions: ordered.length,
      totalIssues: totals.noAdmin,
    })
  }

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* 1. Top Header with Title & Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Institutions</h1>
          <p className="mt-1 font-dash text-dash-sm text-muted">
            Manage all schools and institutions on the Kalqy platform.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-dash-xs font-bold text-white shadow-card hover:bg-primary-hover transition-colors cursor-pointer"
          >
            <span className="text-sm font-extrabold">+</span>
            <span>Add Institution</span>
          </button>
        </div>
      </div>

      <PageDateFilterBar
        title="Institutions Filter Period"
        fromDate={fromDate}
        toDate={toDate}
        onApply={(from, to) => {
          setFromDate(from)
          setToDate(to)
        }}
        onExportExcel={handleSchoolsExportExcel}
        onExportCsv={handleSchoolsExportCsv}
      />

      {/* Top Warning Banner if Schools have no admin */}
      {totals.noAdmin > 0 && (
        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 p-4 px-5 flex flex-wrap items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 font-extrabold text-dash-xs">
              !
            </div>
            <div>
              <h3 className="text-dash-sm font-extrabold text-amber-950">
                {t.schoolsNoAdmin(totals.noAdmin)}
              </h3>
              <p className="text-dash-xs text-amber-800/90 mt-0.5">
                Open the school and create its first admin. Until then the account exists and nobody at live can get in.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setFilter('ALL')
              setQuery('')
            }}
            className="bg-white border border-amber-300 text-amber-900 font-bold px-4 py-2 rounded-full hover:bg-amber-100/50 text-dash-xs shadow-xs transition-colors cursor-pointer"
          >
            View schools
          </button>
        </div>
      )}

      {/* 2. Top Stat Cards Grid (6 Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: Total Institutions */}
        <div
          onClick={() => setFilter('ALL')}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex flex-col justify-between cursor-pointer transition-all hover:border-indigo-500 hover:shadow-md ${
            filter === 'ALL' ? 'border-indigo-600 ring-2 ring-indigo-500/20' : 'border-line'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <SchoolIcon size={20} />
            </div>
            <span className="text-[10px] font-extrabold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">Click to filter</span>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Institutions</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {String(totals.all)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {`${String(totals.noAdmin)} with nobody who can sign in`}
            </span>
          </div>
        </div>

        {/* Card 2: Active Institutions */}
        <div
          onClick={() => setFilter('ACTIVE')}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex flex-col justify-between cursor-pointer transition-all hover:border-emerald-500 hover:shadow-md ${
            filter === 'ACTIVE' ? 'border-emerald-600 ring-2 ring-emerald-500/20' : 'border-line'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <CheckIcon size={20} />
            </div>
            <span className="text-[10px] font-extrabold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">Click to filter</span>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Active Institutions</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {String(totals.active)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Signed off and in use"}
            </span>
          </div>
        </div>

        {/* Card 3: Trial Institutions */}
        <div
          onClick={() => setFilter('ONBOARDING')}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex flex-col justify-between cursor-pointer transition-all hover:border-amber-500 hover:shadow-md ${
            filter === 'ONBOARDING' ? 'border-amber-600 ring-2 ring-amber-500/20' : 'border-line'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <InfoIcon size={20} />
            </div>
            <span className="text-[10px] font-extrabold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">Click to filter</span>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Trial Institutions</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {String(totals.onboarding)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Created or onboarding, not yet active"}
            </span>
          </div>
        </div>

        {/* Card 4: Inactive Institutions */}
        <div
          onClick={() => setFilter('SUSPENDED')}
          className={`rounded-2xl border bg-surface p-4 shadow-card flex flex-col justify-between cursor-pointer transition-all hover:border-rose-500 hover:shadow-md ${
            filter === 'SUSPENDED' ? 'border-rose-600 ring-2 ring-rose-500/20' : 'border-line'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <AlertIcon size={20} />
            </div>
            <span className="text-[10px] font-extrabold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md">Click to filter</span>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Inactive Institutions</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {String(totals.suspended + totals.archived)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {`${String(totals.suspended)} suspended · ${String(totals.archived)} archived`}
            </span>
          </div>
        </div>

        {/* Card 5: Total Students */}
        <div
          onClick={() => setQuery('students')}
          className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between cursor-pointer transition-all hover:border-indigo-500 hover:shadow-md"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">
              <ChildrenIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Students</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(overviewResource.data?.enrolment.seats ?? null)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Children holding a seat"}
            </span>
          </div>
        </div>

        {/* Card 6: Total Teachers */}
        <div
          onClick={() => setQuery('teachers')}
          className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between cursor-pointer transition-all hover:border-indigo-500 hover:shadow-md"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="h-10 w-10 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center shrink-0">
              <PointIcon size={20} />
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Teachers</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(overviewResource.data?.teachers.active ?? null)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Active teacher accounts"}
            </span>
          </div>
        </div>
      </div>


      {/* 3. Filter Toolbar (Search + Status + Plan + Location + Student Count + More Filters) */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[260px]">
          <input
            type="search"
            placeholder="Search by institution name, domain..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
            }}
            className="w-full rounded-xl border border-line bg-white px-4 py-2 text-dash-xs pl-10 pr-4 focus:border-primary focus:outline-none transition-colors"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted" htmlFor="inst-status-filter">
              Status
            </label>
            <select
              id="inst-status-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value as SchoolFilter)}
              className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="ONBOARDING">Trial / Onboarding</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Plan</label>
            <select className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer">
              <option>All Plans</option>
              <option>Premium</option>
              <option>Standard</option>
              <option>Basic</option>
              <option>Trial</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Location</label>
            <select className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer">
              <option>All Locations</option>
              <option>Bangalore, KA</option>
              <option>Pune, MH</option>
              <option>Delhi, DL</option>
              <option>Hyderabad, TG</option>
              <option>Mumbai, MH</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-dash-xs font-bold text-muted">Student Count</label>
            <select className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer">
              <option>All</option>
              <option>&lt; 500</option>
              <option>500 - 1000</option>
              <option>&gt; 1000</option>
            </select>
          </div>

          <button
            type="button"
            className="flex items-center gap-1.5 rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs cursor-pointer"
          >
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* 4. Institutions Data Table */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {/*
              ✅ THE NUMBER OF INSTITUTIONS THERE ARE. This read `(124)` — a
              literal from the mockup this screen was built from, sitting above a
              table of seven real schools. It says so beside the filter when one
              is narrowing the list, because "(2)" over a seven-school platform
              is otherwise its own small lie.
            */}
            <h2 className="text-dash-lg font-extrabold text-ink">
              Institutions ({visibleSchools.length}
              {visibleSchools.length === schoolsTableData.length
                ? ''
                : ` of ${String(schoolsTableData.length)}`}
              )
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-dash-xs font-bold text-muted">Sort by:</span>
            <select className="rounded-xl border border-line bg-white px-3.5 py-1.5 text-dash-xs text-ink focus:border-primary focus:outline-none cursor-pointer font-bold">
              <option>Recently Added</option>
              <option>Institution Name</option>
              <option>Student Count</option>
              <option>Engagement Rate</option>
            </select>

            {/* View layout toggles */}
            <div className="flex items-center border border-line rounded-lg overflow-hidden bg-white">
              <button type="button" className="p-1.5 bg-slate-100 text-slate-800 font-bold">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button type="button" className="p-1.5 text-slate-400 hover:text-slate-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div>
          <TableCard>
            <table className="w-full border-collapse">
              <Thead
                headers={[
                  '#',
                  'Institution Name',
                  'Domain',
                  'Plan',
                  'Location',
                  'Students',
                  'Teachers',
                  'Active Users (7D)',
                  'Engagement Rate',
                  'Subscription',
                  'Renewal Date',
                  'Status',
                  'Actions',
                ]}
              />
              <tbody>
                {pagedSchools.map((school: SchoolsTableRow, idx: number) => {
                  return (
                    <Tr key={school.id} selected={school.id === openId}>
                      {/* Continuing across pages — row 11 is 11, not 1 again. */}
                      <Td bold className="text-muted font-mono">
                        {(schoolPage - 1) * pageSize + idx + 1}
                      </Td>
                      <Td>
                        <div className="flex items-center gap-3">
                          <div>
                            <button
                              type="button"
                              onClick={() => setSelectedSchool({ id: school.id, name: school.name })}
                              className="font-black text-indigo-600 hover:underline block text-left cursor-pointer"
                            >
                              {school.name}
                            </button>
                            <span className="text-[11px] text-muted block font-normal">ID: {school.id}</span>
                          </div>
                        </div>
                      </Td>
                      <Td soft>{school.domain ?? DASH}</Td>
                      <Td>
                        <span className="text-dash-xs font-bold text-muted">{school.plan ?? DASH}</span>
                      </Td>
                      <Td soft>
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{school.loc}</span>
                        </span>
                      </Td>
                      <Td num>{school.students?.toLocaleString() ?? DASH}</Td>
                      <Td num>{school.teachers?.toLocaleString() ?? DASH}</Td>
                      <Td num>{school.active7d?.toLocaleString() ?? DASH}</Td>
                      <Td soft>{school.rate === null ? DASH : `${school.rate.toFixed(1)}%`}</Td>
                      <Td>
                        <span className="text-dash-xs font-bold text-muted">{school.sub ?? DASH}</span>
                      </Td>
                      <Td soft>{school.renewal ?? DASH}</Td>
                      <Td>
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-dash-xs font-bold ${
                            school.status === 'ACTIVE'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {school.status}
                        </span>
                      </Td>
                      <Td>
                        <div className="relative group">
                          <button
                            type="button"
                            onClick={() => setOpenId(school.id === openId ? null : school.id)}
                            className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>

                          {openId === school.id && (
                            <div className="absolute right-0 top-full mt-1 w-44 rounded-xl border border-line bg-white shadow-xl z-20 py-1.5 text-dash-xs font-bold">
                              <button
                                type="button"
                                onClick={() => {
                                  if (onActAs) onActAs(school.id, 'Super Admin support session')
                                  setOpenId(null)
                                }}
                                className="w-full text-left px-3 py-1.5 text-indigo-600 hover:bg-indigo-50"
                              >
                                Act As Admin
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedSchool({ id: school.id, name: school.name })
                                  setOpenId(null)
                                }}
                                className="w-full text-left px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                              >
                                View Dashboard
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedSchool({ id: school.id, name: school.name })
                                  setOpenId(null)
                                }}
                                className="w-full text-left px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                              >
                                Edit Institution
                              </button>
                            </div>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  )
                })}
              </tbody>
            </table>
          </TableCard>

          {/*
            ✅ THE PAGER IS THE ROWS' NOW, NOT THE MOCKUP'S. What stood here was
            static markup — buttons reading 1, 2, 3, 4, 5, an ellipsis and a 13,
            with "Showing 1 to 10 of 124 entries" beside them and a rows-per-page
            select wired to nothing. None of it had a handler or a piece of
            state; the 124 and the 13 were literals from the design this screen
            was traced from, above a table of seven real institutions.

            🔴 `Pagination` DRAWS NOTHING AT ALL FOR A SINGLE PAGE, which is the
            honest answer for a platform with seven schools — and is exactly what
            a hardcoded "13" could never be.
          */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-dash-xs text-muted">
            <label className="flex items-center gap-2 font-bold" htmlFor="schools-page-size">
              <span>Rows</span>
              <select
                id="schools-page-size"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                }}
                className="rounded-lg border border-line bg-white px-2 py-1 text-dash-xs font-bold text-ink cursor-pointer"
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </label>
          </div>

          <Pagination
            currentPage={schoolPage}
            totalPages={schoolTotalPages}
            onPageChange={setSchoolPage}
            totalItems={visibleSchools.length}
            pageSize={pageSize}
          />
        </div>
      </div>

      {/* 5. Bottom Analytics Cards Grid (Matching Mockup 1:1) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Card 1: Institutions by Plan */}
        <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Institutions by Plan</h3>
            <button type="button" className="text-dash-xs font-bold text-primary hover:underline cursor-pointer">
              View Report
            </button>
          </div>

          <div className="flex items-center justify-between gap-6 my-auto">
            <div className="relative h-36 w-36 shrink-0 flex items-center justify-center">
              <svg className="h-full w-full transform -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#f1f5f9" strokeWidth="3.8" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                {/* ✅ The schools there are, not the 124 the mockup drew. */}
                <span className="text-dash-lg font-extrabold text-ink leading-none">
                  {schoolsTableData.length}
                </span>
                <span className="text-[11px] font-bold text-muted mt-0.5">Total</span>
              </div>
            </div>

            {/*
              🔴 THE FOUR PLAN NAMES ARE GONE, AND THEIR EM DASHES WITH THEM.
              Premium / Standard / Basic / Trial each read "—" because NOTHING ON
              THIS PLATFORM RECORDS A BILLING PLAN: `SchoolSummary` has no plan
              field, so `schoolsTableData` sets `plan: null` for every row and
              says so. Four named tiers with a dash beside each read as four
              tiers that exist and happen to be empty — a ring around a total
              nothing divides.
            */}
            <div className="flex flex-col gap-2 w-full text-dash-xs">
              <p className="font-semibold text-muted">
                Billing plans are not recorded yet, so there is nothing to split
                this total by. The Subscriptions screen is where a plan will be
                held once one exists.
              </p>
            </div>
          </div>
        </div>

        {/* Card 2: Institutions by Location */}
        <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Institutions by Location</h3>
            <button type="button" className="text-dash-xs font-bold text-primary hover:underline cursor-pointer">
              View Report
            </button>
          </div>

          <div className="space-y-2 text-dash-xs my-auto">
            <div className="flex items-center justify-between font-bold text-ink">
              <span>Karnataka</span>
              <span className="text-muted">—</span>
            </div>
            <div className="flex items-center justify-between font-bold text-ink">
              <span>Maharashtra</span>
              <span className="text-muted">—</span>
            </div>
            <div className="flex items-center justify-between font-bold text-ink">
              <span>Delhi</span>
              <span className="text-muted">—</span>
            </div>
            <div className="flex items-center justify-between font-bold text-ink">
              <span>Tamil Nadu</span>
              <span className="text-muted">—</span>
            </div>
            <div className="flex items-center justify-between font-bold text-ink">
              <span>Uttar Pradesh</span>
              <span className="text-muted">—</span>
            </div>
            <div className="flex items-center justify-between font-bold text-ink">
              <span>Other States</span>
              <span className="text-muted">—</span>
            </div>
          </div>
        </div>

        {/* Card 3: Subscription Status */}
        <div className="lg:col-span-4 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            {/*
              ✅ "SUBSCRIPTION STATUS" BECAME "BY STATUS", AND THE NUMBERS ARE
              REAL BECAUSE THIS SCREEN ALREADY HAD THEM. `summariseSchools` folds
              the lifecycle of every school on mount — onboarding, active,
              suspended, archived — and `totals` has sat unread beside a card
              printing Active / Trial / Expiring Soon / Inactive with a dash on
              each. Three of those four are not states this platform has; the
              lifecycle is, it is what the Status column beside them shows, and
              `admin-panel.md` §3 is where suspension's billing meaning lives.
            */}
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Institutions by status</h3>
          </div>

          <div className="flex items-center justify-between gap-6 my-auto">
            <div className="relative h-36 w-36 shrink-0 flex items-center justify-center">
              <svg className="h-full w-full transform -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#f1f5f9" strokeWidth="3.8" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                <span className="text-dash-lg font-extrabold text-ink leading-none">{totals.all}</span>
                <span className="text-[11px] font-bold text-muted mt-0.5">Total</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 w-full text-dash-xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Active</span>
                <span className="font-bold text-ink tabular">{totals.active}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Onboarding</span>
                <span className="font-bold text-ink tabular">{totals.onboarding}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Suspended</span>
                <span className="font-bold text-ink tabular">{totals.suspended}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" />Archived</span>
                <span className="font-bold text-ink tabular">{totals.archived}</span>
              </div>
              {/* 🔴 A SCHOOL NOBODY ADMINISTERS IS A FINDING, NOT A STATUS, so it
                  sits below the split rather than inside it — it overlaps the
                  four above and would make them sum to more than the total. */}
              {totals.noAdmin > 0 && (
                <div className="flex items-center justify-between border-t border-line pt-2">
                  <span className="font-bold text-pill-warning-fg">No admin yet</span>
                  <span className="font-bold text-pill-warning-fg tabular">{totals.noAdmin}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {open !== null && (
        <div className="mt-8">
          <SchoolDetail
            school={open}
            lang={lang}
            auth={auth}
            onChanged={resource.retry}
            onActAs={onActAs}
            onClose={() => setOpenId(null)}
          />
        </div>
      )}

      {showAddModal && (
        <div
          /*
           * ⚠️ THE OVERLAY SCROLLS AND DOES NOT ALIGN; THE WRAPPER INSIDE IT
           * ALIGNS AND DOES NOT SCROLL. A container that does both puts half of
           * a too-tall dialog above its own scroll origin, where the title and
           * the first field are unreachable rather than merely scrolled past —
           * on a phone held sideways that is most of the forms in this tier.
           * `HomeScreen` carries the measurement and why `safe center` is not
           * the answer (Chromium reports `center` regardless).
           */
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs"
          onClick={() => setShowAddModal(false)}
        >
          {/* Aligns, does not scroll — see the overlay's note above. */}
          <div className="flex min-h-full items-center justify-center p-4">
          <div
            /*
             * ⚠️ NO SECOND SCROLLER. The panel used to be `max-h-[90vh]
             * overflow-y-auto` INSIDE an overlay that also scrolled, which is two
             * nested scroll regions for one dialog: a trackpad flick near the
             * edge moved the backdrop while the form stood still. The overlay
             * owns the scroll now and the panel simply grows.
             */
            className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-line p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <CreateSchoolForm
              lang={lang}
              auth={auth}
              onCreated={resource.retry}
              onClose={() => setShowAddModal(false)}
            />
          </div>
          </div>
        </div>
      )}
    </section>
  )
}


function CredentialCard({
  admin,
  schoolId,
  lang,
  auth,
  onDone,
}: {
  admin: ProvisionedAdmin
  /** Needed only for the re-send, which is addressed by school and by user. */
  schoolId: string
  lang: Lang
  auth?: StaffAuth
  onDone: () => void
}) {
  const t = PLATFORM_COPY[lang]

  /*
   * 🔴 THE STATE THAT MAKES A FAILED SEND RECOVERABLE FROM THIS CARD.
   *
   * `provisionByInvite` calls a failed delivery *"a recoverable state with an
   * obvious next action"* — and until `resendAdminInvite` existed there was no
   * way to perform it, so this card reported the failure and offered nothing.
   * The operator's remaining move was to create a second admin: one school, two
   * accounts, one working mailbox.
   *
   * ⚠️ `sent` STARTS AS THE SERVER'S ANSWER AND THEN BECOMES THIS BROWSER'S. A
   * re-send that works has to change what the card says, or the operator presses
   * it again — and each press revokes the link the last one sent.
   */
  const [resend, setResend] = useState<'IDLE' | 'SENDING' | 'SENT' | 'FAILED'>('IDLE')
  const sendAgain = useCallback(async () => {
    setResend('SENDING')
    try {
      const out = await resendAdminInvite(schoolId, admin.userId, auth)
      setResend(out.delivery === 'SENT' ? 'SENT' : 'FAILED')
    } catch {
      setResend('FAILED')
    }
  }, [schoolId, admin.userId, auth])

  /*
   * 🔴 CHANGE 19 §1 — THREE OUTCOMES, THREE DIFFERENT NEXT ACTIONS, AND THE
   * MIDDLE ONE IS WHY THIS BRANCHES AT ALL.
   *
   *   SENT          — nothing for the operator to do. Say so and stop.
   *   FAILED        — the school, the account and the invite are all real; only
   *                   the delivery failed. Reporting this as success is how an
   *                   operator closes the tab believing a head teacher has a
   *                   link they never received.
   *   NOT_ATTEMPTED — the password path. Hand it over, as before.
   */
  const emailed = admin.passwordSource === 'INVITE'
  /* ⚠️ THE CARD FOLLOWS THE LATEST ATTEMPT, NOT THE FIRST. A successful re-send
     must stop the card saying the mail failed; a re-send that also failed must
     not start claiming it worked. */
  const failed = resend === 'IDLE' ? admin.delivery === 'FAILED' : resend === 'FAILED'
  const tone = failed ? 'warning' : 'success'

  return (
    <div
      className="mt-4 max-w-prose rounded-xl p-4"
      style={{
        background: `var(--color-pill-${tone}-bg)`,
        border: `1px solid var(--color-pill-${tone}-line)`,
      }}
    >
      <h3
        className="text-dash-sm font-extrabold"
        style={{ color: `var(--color-pill-${tone}-fg)` }}
      >
        {emailed
          ? failed
            ? t.credentialMailFailedTitle
            : t.credentialMailedTitle
          : t.credentialChosenTitle}
      </h3>
      <p className="mt-1 text-dash-xs" style={{ color: `var(--color-pill-${tone}-fg)` }}>
        {admin.name} {'·'} {admin.email}
      </p>

      <p className="mt-4 text-dash-xs" style={{ color: `var(--color-pill-${tone}-fg)` }}>
        {emailed
          ? failed
            ? t.credentialMailFailedBody
            : t.credentialMailedBody(
                admin.inviteExpiresAt === null
                  ? ''
                  : new Date(admin.inviteExpiresAt).toLocaleString(),
              )
          : t.credentialChosenBody}
      </p>
      <p className="mt-2 text-dash-xs" style={{ color: `var(--color-pill-${tone}-fg)` }}>
        {emailed ? t.credentialMailedAdvice : t.credentialChangeAdvice}
      </p>

      {admin.existingAdminCount > 0 && (
        <p className="mt-4">
          <StatusPill tone="danger" icon={<InfoIcon size={14} />}>
            {t.credentialExisting(admin.existingAdminCount)}
          </StatusPill>
        </p>
      )}

      {/* 🔴 OFFERED ONLY WHERE THE SERVER WOULD ACCEPT IT — D4's lesson: the UI
          must not offer a button that throws. `resendAdminInvite` refuses an
          admin who has already set a password, because that is a password reset
          rather than an invitation. */}
      {canResendInvite(admin) && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={resend === 'SENDING'}
            onClick={() => void sendAgain()}
            className="rounded-full bg-white border border-line px-5 py-2 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs cursor-pointer disabled:opacity-50"
          >
            {resend === 'SENDING' ? 'Sending...' : 'Send the link again'}
          </button>
          {/* ⚠️ THE COST OF PRESSING IT, SAID BEFORE IT IS PRESSED. `issue`
              revokes every unclaimed invite first, so a link already in an inbox
              stops working the moment a new one is sent. */}
          <span className="text-dash-xs" style={{ color: `var(--color-pill-${tone}-fg)` }}>
            {resend === 'SENT'
              ? 'Sent. Any earlier link has stopped working.'
              : resend === 'FAILED'
                ? 'That did not go either. Check the address, or try again shortly.'
                : 'This replaces any link already sent to them.'}
          </span>
        </div>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={onDone}
          className="rounded-full bg-white border border-line px-5 py-2 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs"
        >
          {t.credentialDone}
        </button>
      </div>
    </div>
  )
}

function PasswordFields({
  idPrefix,
  lang,
  password,
  confirm,
  state,
  disabled,
  onPassword,
  onConfirm,
}: {
  idPrefix: string
  lang: Lang
  password: string
  confirm: string
  state: PasswordState
  disabled: boolean
  onPassword: (value: string) => void
  onConfirm: (value: string) => void
}) {
  const t = PLATFORM_COPY[lang]
  return (
    <>
      <h4 className="mt-4 text-dash-sm font-extrabold text-ink">{t.passwordSection}</h4>
      <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.passwordHint}</p>

      <div className="mt-3 grid max-w-xl gap-4 sm:grid-cols-2">
        <Field label={t.passwordField} htmlFor={`${idPrefix}-password`} width="full">
          <PasswordInput
            id={`${idPrefix}-password`}
            autoComplete="new-password"
            value={password}
            disabled={disabled}
            maxLength={OPERATOR_PASSWORD_MAX}
            onChange={(e) => onPassword(e.target.value)}
          />
        </Field>
        <Field label={t.passwordConfirm} htmlFor={`${idPrefix}-password-confirm`} width="full">
          <PasswordInput
            id={`${idPrefix}-password-confirm`}
            autoComplete="new-password"
            value={confirm}
            disabled={disabled}
            maxLength={OPERATOR_PASSWORD_MAX}
            onChange={(e) => onConfirm(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-3 max-w-prose">
        {state.kind === 'empty' && (
          <Note tone="info" icon={<InfoIcon size={16} />}>
            {t.passwordRequiredNote}
          </Note>
        )}
        {state.kind === 'ready' && (
          <Note tone="success" icon={<CheckIcon size={16} />}>
            {t.passwordReadyNote}
          </Note>
        )}
        {state.kind === 'problem' && (
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {passwordProblemText(state.problem, lang)}
          </Note>
        )}
      </div>
    </>
  )
}

/**
 * §3's onboard action, as the flow it always described.
 *
 * 🔴 IT WAS ONE FORM AND IT IS FOUR STEPS, AND THE THIRD STEP IS THE REASON.
 * `admin-panel.md` §3 defines onboarding as *"create school, set plan, invite
 * the school admin"* — three decisions — and this screen only ever collected
 * two. Setting a plan meant leaving here, opening F3, finding the school just
 * created in a second table and starting again; the gap between those two
 * requests is where a school with an admin and no plan comes from, and nobody
 * finds out until somebody asks why it was never invoiced.
 *
 * 🔴 EVERY DECISION BELOW IS `platformModel.ts`'s, NOT THIS FILE'S. The repo has
 * no DOM renderer (OD-60), so a rule written into a `disabled=` expression is a
 * rule no test can reach — and the rules here decide whether a school is
 * billable, whether anybody can sign in, and how long it is free for. This
 * component reads state and paints; `stepBlockers`, `onboardingBody` and
 * `onboardingOutcome` decide.
 *
 * ⚠️ THE STEPS ARE NOT A PROGRESS BAR OVER ONE FORM. Each one can be finished by
 * doing nothing: a school with no admin is real, and so is a school with no
 * plan. What the steps buy is that *"what happens when the free period ends"*
 * arrives as its own question instead of as two fields below a password box.
 */
function CreateSchoolForm({
  lang,
  auth,
  onCreated,
  onClose,
}: {
  lang: Lang
  auth?: StaffAuth
  onCreated: () => void
  onClose?: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [step, setStep] = useState<OnboardingStep>('INSTITUTION')
  const [draft, setDraft] = useState<OnboardingDraft>(() => emptyOnboardingDraft('KL'))
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)
  const [issued, setIssued] = useState<CreatedSchool | null>(null)

  /*
   * ⚠️ THE PLAN LIST IS FETCHED ONCE, WHEN THE WIZARD OPENS, AND NOT PER STEP.
   * It is the operator's own catalogue — small, and unchanged between step one
   * and step three of the same sitting. Re-reading it on the plan step would put
   * a spinner in the middle of a flow for a list that was already in memory.
   */
  const plans = useResource<PlanRow[]>((signal) => getPlans({ ...auth, signal }), ['plans'])

  const set = useCallback(<K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }))
  }, [])

  const pw = useMemo(
    () =>
      passwordState(draft.password, draft.confirm, {
        email: draft.adminEmail.trim(),
        schoolName: draft.name.trim(),
      }),
    [draft.password, draft.confirm, draft.adminEmail, draft.name],
  )

  const blockers = useMemo(() => stepBlockers(step, draft, pw), [step, draft, pw])
  const blocked = blockers.length > 0

  /*
   * ⚠️ RECOMPUTED ON EVERY RENDER RATHER THAN FROZEN WHEN THE WIZARD OPENED. The
   * date on the review screen has to be the date the request will carry, and a
   * wizard left open across midnight would otherwise show one date and send
   * another. `onboardingBody` takes `now` for the same reason.
   */
  const trialEnd = useMemo(
    () => trialEndsOn(new Date(), draft.trialDays),
    [draft.trialDays],
  )

  const chosenPlan = useMemo(
    () => (plans.data ?? []).find((p) => p.id === draft.planId) ?? null,
    [plans.data, draft.planId],
  )

  const submit = useCallback(async () => {
    setBusy(true)
    setFailure(null)
    try {
      const created = await createSchool(onboardingBody(draft, pw, new Date()), auth)
      setIssued(created)
      onCreated()
    } catch (err) {
      setFailure(classifyWriteError(err))
    } finally {
      setBusy(false)
    }
  }, [draft, pw, auth, onCreated])

  if (issued !== null) {
    return (
      <OnboardingResult
        created={issued}
        lang={lang}
        auth={auth}
        onDone={() => {
          setIssued(null)
          onClose?.()
        }}
      />
    )
  }

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          /*
           * 🔴 ENTER ADVANCES; IT DOES NOT SUBMIT — EXCEPT ON THE LAST STEP.
           * A four-step form whose first text field submits the whole thing on
           * Enter would onboard a school with no admin and no plan, from a
           * keystroke that means "next field" everywhere else.
           */
          if (step === 'REVIEW') {
            void submit()
            return
          }
          const next = nextStep(step)
          if (next !== null && !blocked) setStep(next)
        }}
        className="space-y-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line pb-4">
          <div>
            <h2 className="text-dash-lg font-extrabold text-ink">Onboard Institution</h2>
            <p className="text-dash-xs text-muted mt-0.5">
              A school, its first sign-in, and what happens when the free trial ends.
            </p>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted hover:text-ink hover:bg-canvas transition-colors text-dash-md font-bold cursor-pointer"
              aria-label="Close modal"
            >
              &times;
            </button>
          )}
        </div>

        <StepRail current={step} />

        {step === 'INSTITUTION' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="School name *" htmlFor="school-name" width="full">
              <Input
                id="school-name"
                placeholder="e.g. Sunrise Public School"
                value={draft.name}
                onChange={(e) => set('name', e.target.value)}
                maxLength={120}
                className="rounded-xl border border-line bg-white"
              />
            </Field>

            <Field
              label="State *"
              htmlFor="school-region"
              /* 🔴 THE ONE FIELD ON THIS SCREEN THAT CANNOT BE FIXED LATER
                 WITHOUT CONSEQUENCE — X2 hangs a child's deletion date off it. */
              hint="Sets the data-retention rules that apply to this school's children."
              width="full"
            >
              <Select
                id="school-region"
                value={draft.region}
                onChange={(e) => set('region', e.target.value as Region)}
                className="rounded-xl border border-line bg-white"
              >
                {REGIONS_BY_NAME.map((r) => (
                  <option key={r} value={r}>
                    {REGION_LABEL[r]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}

        {step === 'ADMIN' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-dash-md font-extrabold text-ink">Its first sign-in</h3>
              <p className="text-dash-xs text-muted mt-0.5">
                Optional. Leave both blank and the school arrives with nobody able to sign in
                yet, which the schools list shows at the top until it is fixed.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Their name" htmlFor="new-school-admin-name" width="full">
                <Input
                  id="new-school-admin-name"
                  placeholder="e.g. John Doe"
                  value={draft.adminName}
                  onChange={(e) => set('adminName', e.target.value)}
                  maxLength={120}
                  className="rounded-xl border border-line bg-white"
                />
              </Field>
              <Field
                label="Their email"
                htmlFor="new-school-admin-email"
                hint="The set-up link goes here. It is also the address they sign in with."
                width="full"
              >
                <Input
                  id="new-school-admin-email"
                  type="email"
                  autoComplete="off"
                  placeholder="e.g. john@school.com"
                  value={draft.adminEmail}
                  onChange={(e) => set('adminEmail', e.target.value)}
                  maxLength={254}
                  className="rounded-xl border border-line bg-white"
                />
              </Field>
            </div>

            {wantsAdmin(draft) && (
              <>
                {/* 🔴 §1's CHOICE, ON THE SCREEN. Emailing a link is the default:
                    it is the only path where nobody — not the operator, not this
                    browser, not a log — ever holds a credential that works. */}
                <fieldset className="pt-2 border-t border-line">
                  <legend className="text-dash-md font-extrabold text-ink">
                    How do they get in?
                  </legend>
                  <div className="mt-2 max-w-xl">
                    <CheckboxRow
                      checked={draft.delivery === 'EMAIL'}
                      disabled={busy}
                      hint="They pick their own password from a single-use link that stops working after 72 hours. Nobody else ever knows it."
                      onChange={(on) => {
                        set('delivery', on ? 'EMAIL' : 'PASSWORD')
                      }}
                    >
                      Email them a set-up link
                    </CheckboxRow>
                    {draft.delivery === 'PASSWORD' && (
                      <p className="mt-1 px-3 text-dash-xs text-muted">
                        {/* ⚠️ The cost of the other path, said where it is chosen. */}
                        You will have to pass this password to them yourself, and it keeps
                        working until they change it.
                      </p>
                    )}
                  </div>
                </fieldset>

                {draft.delivery === 'PASSWORD' && (
                  <PasswordFields
                    idPrefix="new-school"
                    lang={lang}
                    password={draft.password}
                    confirm={draft.confirm}
                    state={pw}
                    disabled={busy}
                    onPassword={(v) => set('password', v)}
                    onConfirm={(v) => set('confirm', v)}
                  />
                )}
              </>
            )}
          </div>
        )}

        {step === 'PLAN' && (
          <PlanStep
            draft={draft}
            plans={plans}
            trialEnd={trialEnd}
            busy={busy}
            onChange={set}
          />
        )}

        {step === 'REVIEW' && (
          <ReviewStep
            draft={draft}
            plan={chosenPlan}
            trialEnd={trialEnd}
            password={pw}
            onEdit={setStep}
          />
        )}

        {/* 🔴 THE REFUSAL IS A SENTENCE, NOT A GREYED-OUT BUTTON. A disabled Next
            with no explanation is a form that has decided something about your
            input and will not say what. */}
        {blocked && (
          <div className="max-w-prose space-y-2">
            {blockers.map((problem) => (
              <Note key={problem} tone="warning" icon={<AlertIcon size={16} />}>
                {problem}
              </Note>
            ))}
          </div>
        )}

        <div className="pt-4 border-t border-line flex flex-wrap items-center justify-between gap-3">
          <div>
            {previousStep(step) !== null && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  const back = previousStep(step)
                  if (back !== null) setStep(back)
                }}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-ink hover:bg-canvas transition-colors text-dash-xs cursor-pointer disabled:opacity-50"
              >
                Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-line bg-white font-bold text-ink hover:bg-canvas transition-colors text-dash-xs cursor-pointer"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={busy || blocked}
              className="bg-[#E6007A] text-white font-bold px-6 py-2.5 rounded-full hover:bg-[#C40068] transition-colors shadow-xs disabled:opacity-50 text-dash-xs cursor-pointer"
            >
              {step === 'REVIEW' ? (busy ? t.creating : 'Onboard Institution') : 'Continue'}
            </button>
          </div>
        </div>

        {failure !== null && (
          <div className="mt-4">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failureText(failure, lang, t.nameTaken)}
            </Note>
          </div>
        )}
      </form>
    </div>
  )
}

/** What each step is called, where the operator can see how far they are. */
const STEP_LABEL: Record<OnboardingStep, string> = {
  INSTITUTION: 'Institution',
  ADMIN: 'First sign-in',
  PLAN: 'Plan & trial',
  REVIEW: 'Review',
}

/**
 * ⚠️ IT SHOWS POSITION AND IS NOT A NAVIGATION CONTROL. Clicking step four from
 * step one would skip the two steps whose blockers have not run, and the review
 * screen's own edit links are the way back to a specific step — they exist after
 * every step has been visited, which is the only point at which jumping is safe.
 */
function StepRail({ current }: { current: OnboardingStep }) {
  const index = ONBOARDING_STEPS.indexOf(current)
  return (
    <ol className="flex flex-wrap items-center gap-2 text-dash-xs font-bold">
      {ONBOARDING_STEPS.map((s, i) => {
        const done = i < index
        const here = i === index
        return (
          <li key={s} className="flex items-center gap-2">
            <span
              aria-current={here ? 'step' : undefined}
              className={
                here
                  ? 'rounded-full bg-[#E6007A] text-white px-3 py-1'
                  : done
                    ? 'rounded-full bg-canvas text-ink px-3 py-1'
                    : 'rounded-full bg-canvas text-muted px-3 py-1'
              }
            >
              {String(i + 1)}. {STEP_LABEL[s]}
            </span>
            {i < ONBOARDING_STEPS.length - 1 && (
              <span aria-hidden="true" className="text-muted">
                &rarr;
              </span>
            )}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * The step that did not exist.
 *
 * 🔴 IT REFUSES TO INVENT A PLAN WHEN THERE ARE NONE. `billing.prisma`'s rule is
 * that *"no figure a human did not choose can reach a school"*, so this screen
 * cannot seed a starter plan to make itself usable — it says the catalogue is
 * empty and names the screen that fills it. Onboarding still completes: a school
 * with no plan is a real row, and F3 is where it gets one.
 */
function PlanStep({
  draft,
  plans,
  trialEnd,
  busy,
  onChange,
}: {
  draft: OnboardingDraft
  plans: Resource<PlanRow[]>
  trialEnd: Date
  busy: boolean
  onChange: <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => void
}) {
  const assignable = useMemo(() => (plans.data ?? []).filter((p) => p.isActive), [plans.data])

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-dash-md font-extrabold text-ink">Plan and free trial</h3>
        <p className="text-dash-xs text-muted mt-0.5">
          Optional. A trial is a plan the school is not being charged for yet, so it needs
          one: it is what they move onto when the free period ends.
        </p>
      </div>

      {plans.state === 'ready' && assignable.length === 0 ? (
        <Note tone="info" icon={<InfoIcon size={16} />}>
          There are no plans to choose from yet. Create one under Subscriptions, then come
          back — or finish now and set the plan later.
        </Note>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Plan" htmlFor="onboard-plan" width="full">
              <Select
                id="onboard-plan"
                value={draft.planId}
                disabled={busy || plans.state !== 'ready'}
                onChange={(e) => onChange('planId', e.target.value)}
                className="rounded-xl border border-line bg-white"
              >
                <option value="">No plan yet</option>
                {assignable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} &mdash; {formatPaise(p.perStudentPaise, p.currency)} per student
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Free trial"
              htmlFor="onboard-trial-days"
              hint={
                draft.planId === ''
                  ? 'Choose a plan first.'
                  : `Ends on ${trialEnd.toLocaleDateString()}.`
              }
              width="full"
            >
              <Input
                id="onboard-trial-days"
                type="number"
                min={TRIAL_DAYS_MIN}
                max={TRIAL_DAYS_MAX}
                value={String(draft.trialDays)}
                disabled={busy || draft.planId === ''}
                onChange={(e) => onChange('trialDays', Number(e.target.value))}
                className="rounded-xl border border-line bg-white"
              />
            </Field>
          </div>

          {draft.planId !== '' && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-dash-xs font-bold text-muted">Common lengths:</span>
                {TRIAL_DAY_PRESETS.map((days) => (
                  <button
                    key={days}
                    type="button"
                    disabled={busy}
                    onClick={() => onChange('trialDays', days)}
                    className={
                      draft.trialDays === days
                        ? 'rounded-full bg-ink text-white px-3 py-1 text-dash-xs font-bold cursor-pointer'
                        : 'rounded-full border border-line bg-white text-ink px-3 py-1 text-dash-xs font-bold hover:bg-canvas cursor-pointer'
                    }
                  >
                    {String(days)} days
                  </button>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Place limit"
                  htmlFor="onboard-seat-limit"
                  /* ⚠️ Blank is uncapped, and it has to say so: a ceiling is what
                     a roster import hits, and "0" is a school that may enrol
                     nobody. */
                  hint="Leave blank for no limit. This is a ceiling on enrolment, never a bill."
                  width="full"
                >
                  <Input
                    id="onboard-seat-limit"
                    type="number"
                    min={1}
                    max={SEAT_LIMIT_MAX}
                    placeholder="No limit"
                    value={draft.seatLimit}
                    disabled={busy}
                    onChange={(e) => onChange('seatLimit', e.target.value)}
                    className="rounded-xl border border-line bg-white"
                  />
                </Field>
                <Field
                  label="Note"
                  htmlFor="onboard-note"
                  hint="Goes on the subscription timeline, for whoever reads it months from now."
                  width="full"
                >
                  <Input
                    id="onboard-note"
                    placeholder="e.g. Six-week pilot, signed 12 Aug"
                    value={draft.note}
                    maxLength={300}
                    disabled={busy}
                    onChange={(e) => onChange('note', e.target.value)}
                    className="rounded-xl border border-line bg-white"
                  />
                </Field>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The last thing before three writes happen.
 *
 * 🔴 IT STATES THE ABSENCES AS LOUDLY AS THE PRESENCES. *"No plan"* and *"nobody
 * can sign in yet"* are both legitimate outcomes and both are things an operator
 * meant to fill in and did not — a review screen that only listed what was set
 * would confirm a half-finished onboarding as enthusiastically as a complete one.
 */
function ReviewStep({
  draft,
  plan,
  trialEnd,
  password,
  onEdit,
}: {
  draft: OnboardingDraft
  plan: PlanRow | null
  trialEnd: Date
  password: PasswordState
  onEdit: (step: OnboardingStep) => void
}) {
  const rows: { step: OnboardingStep; label: string; value: string; missing: boolean }[] = [
    {
      step: 'INSTITUTION',
      label: 'School',
      value: draft.name.trim() || 'Not named',
      missing: draft.name.trim().length === 0,
    },
    {
      step: 'INSTITUTION',
      label: 'State',
      value: REGION_LABEL[draft.region],
      missing: false,
    },
    {
      step: 'ADMIN',
      label: 'First sign-in',
      value: wantsAdmin(draft)
        ? `${draft.adminName.trim()} (${draft.adminEmail.trim()})`
        : 'Nobody yet — the school arrives with no login',
      missing: !wantsAdmin(draft),
    },
    {
      step: 'ADMIN',
      label: 'How they get in',
      value: !wantsAdmin(draft)
        ? '—'
        : draft.delivery === 'EMAIL'
          ? 'Emailed a single-use set-up link'
          : password.kind === 'ready'
            ? 'A password you chose, handed over by you'
            : 'Emailed a single-use set-up link',
      missing: false,
    },
    {
      step: 'PLAN',
      label: 'Plan',
      value: plan === null ? 'No plan yet — set one under Subscriptions' : plan.name,
      missing: plan === null,
    },
    {
      step: 'PLAN',
      label: 'Free trial',
      value:
        plan === null
          ? '—'
          : `${String(draft.trialDays)} days, ending ${trialEnd.toLocaleDateString()}`,
      missing: false,
    },
    {
      step: 'PLAN',
      label: 'Place limit',
      value:
        plan === null
          ? '—'
          : draft.seatLimit.trim() === ''
            ? 'No limit'
            : `${draft.seatLimit.trim()} children`,
      missing: false,
    },
  ]

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-dash-md font-extrabold text-ink">Review</h3>
        <p className="text-dash-xs text-muted mt-0.5">
          Onboarding writes the school, the sign-in and the subscription. Everything here
          can be changed afterwards except the state, which sets retention rules.
        </p>
      </div>

      <dl className="divide-y divide-line rounded-xl border border-line bg-white">
        {rows.map((r) => (
          <div
            key={`${r.step}-${r.label}`}
            className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5"
          >
            <dt className="text-dash-xs font-bold text-muted">{r.label}</dt>
            <dd className="flex items-center gap-3">
              <span
                className={
                  r.missing
                    ? 'text-dash-xs font-bold text-muted'
                    : 'text-dash-xs font-extrabold text-ink'
                }
              >
                {r.value}
              </span>
              <button
                type="button"
                onClick={() => onEdit(r.step)}
                className="text-dash-xs font-bold text-[#E6007A] hover:underline cursor-pointer"
              >
                Change
              </button>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * What happened, and the one thing that might still need doing.
 *
 * 🔴 THE SCHOOL EXISTS BY THE TIME THIS RENDERS, ALWAYS. So the loudest thing on
 * the screen is whichever follow-up did NOT happen — `onboardingOutcome` ranks
 * them, and a failed set-up mail wins because the person it was meant for cannot
 * log in to tell anybody it never arrived.
 */
function OnboardingResult({
  created,
  lang,
  auth,
  onDone,
}: {
  created: CreatedSchool
  lang: Lang
  auth?: StaffAuth
  onDone: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const outcome = onboardingOutcome(created)

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
      <div className="border-b border-line pb-3">
        <h2 className="text-dash-md font-extrabold text-ink">{created.name} is onboarded</h2>
        <p className="text-dash-xs text-muted mt-0.5">
          {created.subscription === null
            ? 'No plan is set. Give it one under Subscriptions when the terms are agreed.'
            : created.subscription.trialEndsAt === null
              ? `On ${created.subscription.plan?.name ?? 'a plan'}.`
              : `On ${created.subscription.plan?.name ?? 'a plan'}, free until ${new Date(
                  created.subscription.trialEndsAt,
                ).toLocaleDateString()}.`}
        </p>
      </div>

      {created.admin === null ? (
        <Note tone="info" icon={<InfoIcon size={16} />}>
          Nobody can sign in to this school yet. Open it from the list to add its first
          admin.
        </Note>
      ) : (
        <CredentialCard
          admin={created.admin}
          schoolId={created.id}
          lang={lang}
          auth={auth}
          onDone={onDone}
        />
      )}

      {created.admin === null && (
        <div>
          <button
            type="button"
            onClick={onDone}
            className="rounded-full bg-white border border-line px-5 py-2 text-dash-xs font-bold text-ink hover:bg-canvas transition-colors shadow-xs cursor-pointer"
          >
            {t.credentialDone}
          </button>
        </div>
      )}

      {/* ⚠️ Repeated under the card, because the card's own tone is about the
          credential and this is about the money. */}
      {outcome.kind !== 'MAIL_FAILED' && created.subscription === null && (
        <Note tone="info" icon={<InfoIcon size={16} />}>
          This school has no plan, so nothing will ever be invoiced for it.
        </Note>
      )}
    </div>
  )
}

function SchoolDetail({
  school,
  lang,
  auth,
  onChanged,
  onActAs,
  onClose,
}: {
  school: SchoolSummary
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
  onActAs?: (userId: string, reason: string) => Promise<void>
  onClose?: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)
  const [confirming, setConfirming] = useState<SchoolStatus | null>(null)
  const [activeTab, setActiveTab] = useState('Overview')
  const [showActionsMenu, setShowActionsMenu] = useState(false)

  /*
   * 🔴 SIX TILES, NO REQUEST. This component took a `SchoolSummary` — a name, a
   * status, a class count — and drew 'Total Students', 'Total Teachers',
   * 'Total Parents' and 'Total Classes' as em dashes under 'Not measured yet',
   * while `GET /superadmin/schools/:id/detail` returned every one of them and
   * was already imported into this file for the panel further down.
   */
  const detailResource = useResource<SchoolDetail>(
    (signal) => getSchoolDetail(school.id, { ...auth, signal }),
    ['school-detail-counts', school.id],
  )
  const counts = detailResource.data?.counts ?? null

  const [changes, setChanges] = useState(0)
  const changed = useCallback(() => {
    setChanges((n) => n + 1)
    onChanged()
  }, [onChanged])

  const move = useCallback(
    async (to: SchoolStatus) => {
      setBusy(true)
      setFailure(null)
      try {
        await transitionSchool(school.id, to, auth)
        changed()
      } catch (err) {
        setFailure(classifyWriteError(err))
      } finally {
        setBusy(false)
        setConfirming(null)
      }
    },
    [school.id, auth, changed],
  )

  return (
    <div className="space-y-6 pb-12 font-dash">
      {/* 1. Top Breadcrumb & Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-dash-xs font-bold text-muted mb-1">
            <button
              type="button"
              onClick={onClose}
              className="hover:text-ink hover:underline cursor-pointer flex items-center gap-1"
            >
              <span>← Institutions</span>
            </button>
            <span>&gt;</span>
            <span className="text-ink">{school.name}</span>
          </div>

          <div className="flex items-center gap-3">
            <h1 className="text-dash-2xl font-extrabold text-ink">{school.name}</h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200">
              Active
            </span>
          </div>
          <p className="text-dash-xs font-bold text-muted mt-0.5">
            Academic Year 2025–26
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 bg-white border border-line rounded-xl px-3 py-2 text-dash-xs font-bold text-ink shadow-xs">
            <span>Academic Year 2025–26</span>
            <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="flex items-center gap-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-extrabold px-4 py-2 text-dash-xs shadow-card transition-colors cursor-pointer"
            >
              <span>Actions</span>
              <span className="text-xs">▾</span>
            </button>

            {showActionsMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-line rounded-2xl shadow-xl z-20 py-2 text-dash-xs font-bold text-ink">
                {onActAs && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionsMenu(false)
                      void onActAs(school.id, `Operator viewing ${school.name}`)
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 text-indigo-700 font-extrabold flex items-center gap-2"
                  >
                    <span>🛡 Act As Admin</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowActionsMenu(false)}
                  className="w-full text-left px-4 py-2 hover:bg-canvas flex items-center gap-2"
                >
                  <span>✏ Edit Institute</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowActionsMenu(false)}
                  className="w-full text-left px-4 py-2 hover:bg-canvas flex items-center gap-2"
                >
                  <span>Manage Subscription</span>
                </button>
                <div className="border-t border-line my-1" />
                {onClose && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowActionsMenu(false)
                      onClose()
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-rose-50 text-rose-600 flex items-center gap-2"
                  >
                    <span>✕ Close Overview</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. Top School Profile Overview Card */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column: Logo & Main Details */}
          <div className="lg:col-span-5 flex items-start gap-4">
            <div className="h-16 w-16 rounded-2xl bg-indigo-900 text-amber-400 flex items-center justify-center font-extrabold text-2xl shrink-0 shadow-md border-2 border-amber-400/40">
              <SchoolIcon />
            </div>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-dash-lg font-extrabold text-ink">{school.name}</h2>
                <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                  Verified ✓
                </span>
              </div>
              <p className="text-dash-xs font-semibold text-muted truncate">
                abc.internationalschool@gmail.com
              </p>
              <div className="flex items-center gap-2 text-[11px] font-mono text-muted bg-slate-100/80 px-2.5 py-1 rounded-lg w-fit">
                <span>ID: INST-{school.id.slice(0, 8)}...</span>
                <span className="cursor-pointer hover:text-ink">Copy</span>
              </div>
              <div className="text-dash-xs text-muted font-medium pt-1 space-y-0.5">
                <p>123 Education Street, New Delhi, India - 110001</p>
                <p>+91 98765 43210</p>
              </div>
            </div>
          </div>

          {/* Middle Columns: Meta Fields Grid */}
          <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-dash-xs border-y lg:border-y-0 lg:border-x border-line py-4 lg:py-0 lg:px-6">
            <div>
              <span className="text-muted font-bold block">Principal</span>
              <span className="font-extrabold text-ink block mt-0.5">Dr. Ananya Sharma</span>
            </div>
            {/*
              🔴 FOUR REGISTRATION FACTS ABOUT A SCHOOL, IDENTICAL FOR EVERY
              SCHOOL AN OPERATOR OPENED: established **Jun 15, 2010**, board
              **CBSE**, affiliation number **2730456**, **Day School**. None of
              the four is a column on `School`, so none could ever have been
              read — and an affiliation number is the kind of fact somebody
              copies into a contract.

              ⚠️ WHAT THE PLATFORM ACTUALLY KNOWS ABOUT A SCHOOL IS ON THE WIRE:
              when it was created, when it went active, and the region that
              decides its retention jurisdiction.
            */}
            <div>
              <span className="text-muted font-bold block">Created</span>
              <span className="font-extrabold text-ink block mt-0.5">
                {new Date(school.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-muted font-bold block">Activated</span>
              <span className="font-extrabold text-ink block mt-0.5">
                {school.activatedAt === null
                  ? "Not yet"
                  : new Date(school.activatedAt).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-muted font-bold block">Region</span>
              <span className="font-extrabold text-ink block mt-0.5">{REGION_LABEL[school.region]}</span>
            </div>
            <div>
              <span className="text-muted font-bold block">Academic year</span>
              <span className="font-extrabold text-ink block mt-0.5">
                {detailResource.data?.settings.academicYearLabel ?? "—"}
              </span>
            </div>
            <div>
              <span className="text-muted font-bold block">Total Campuses</span>
              <span className="font-extrabold text-ink block mt-0.5">1</span>
            </div>
            <div>
              <span className="text-muted font-bold block">Timezone</span>
              <span className="font-extrabold text-ink block mt-0.5">Asia/Kolkata (IST)</span>
            </div>
            <div>
              <span className="text-muted font-bold block">Email Domain</span>
              <span className="font-extrabold text-ink block mt-0.5">abcis.edu.in</span>
            </div>
            <div>
              <span className="text-muted font-bold block">Subscription Plan</span>
              <span className="font-extrabold text-indigo-600 block mt-0.5">Premium Annual</span>
            </div>
          </div>

          {/* Right Column: Quick Action Stack */}
          <div className="lg:col-span-2 flex flex-col gap-2 text-dash-xs font-bold">
            <button type="button" className="w-full py-1.5 px-3 rounded-xl border border-line bg-white hover:bg-canvas text-ink text-left flex items-center gap-2 transition-colors">
              <span>✏</span> <span>Edit Institute</span>
            </button>
            <button type="button" className="w-full py-1.5 px-3 rounded-xl border border-line bg-white hover:bg-canvas text-ink text-left flex items-center gap-2 transition-colors">
              <span>Manage Subscription</span>
            </button>
            <button type="button" className="w-full py-1.5 px-3 rounded-xl border border-line bg-white hover:bg-canvas text-ink text-left flex items-center gap-2 transition-colors">
              <span>Manage Users</span>
            </button>
            <button type="button" className="w-full py-1.5 px-3 rounded-xl border border-line bg-white hover:bg-canvas text-ink text-left flex items-center gap-2 transition-colors">
              <span>✉</span> <span>Communication</span>
            </button>
            <button type="button" className="w-full py-1.5 px-3 rounded-xl border border-rose-200 bg-rose-50/50 hover:bg-rose-100/60 text-rose-700 text-left flex items-center gap-2 transition-colors">
              <span>Deactivate Institute</span>
            </button>
          </div>
        </div>
      </div>

      {/* 3. Sub-Navigation Tabs Bar */}
      <div className="border-b border-line flex items-center gap-2 overflow-x-auto text-dash-xs font-extrabold">
        {[
          'Overview', 'Users', 'Academics', 'Finance', 'Exams',
          'Attendance', 'Library', 'Transport', 'Hostel', 'Communication',
          'Reports', 'Settings'
        ].map((tab) => {
          const isSelected = activeTab === tab
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 transition-colors border-b-2 whitespace-nowrap cursor-pointer ${
                isSelected
                  ? 'border-primary text-primary bg-primary/5 rounded-t-xl'
                  : 'border-transparent text-muted hover:text-ink'
              }`}
            >
              {tab}
            </button>
          )
        })}
      </div>

      {/* 4. Top 6 Metric Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Total Students */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Students</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(counts?.students ?? null)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {"Children holding a seat"}
            </span>
            <button type="button" className="text-dash-xs font-extrabold text-primary hover:underline flex items-center gap-1">
              <span>View all students</span> <span>→</span>
            </button>
          </div>
        </div>

        {/* Total Teachers */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Teachers</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(counts?.teachers ?? null)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {counts === null ? "Loading…" : `${String(counts.admins)} admins · ${String(counts.coordinators)} coordinators`}
            </span>
            <button type="button" className="text-dash-xs font-extrabold text-primary hover:underline flex items-center gap-1">
              <span>View all teachers</span> <span>→</span>
            </button>
          </div>
        </div>

        {/* Total Parents */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Children who played</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(counts?.activeStudents ?? null)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {counts === null ? "Loading…" : `of ${String(counts.students)} in the last ${String(counts.engagementWindowDays)} days`}
            </span>
            <button type="button" className="text-dash-xs font-extrabold text-primary hover:underline flex items-center gap-1">
              <span>View all parents</span> <span>→</span>
            </button>
          </div>
        </div>

        {/* Total Classes */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted block">Total Classes</span>
            <span className="text-dash-xl font-extrabold text-ink tabular block my-1">
              {tileValue(counts?.classes ?? null)}
            </span>
            <span className="text-dash-xs font-medium text-muted">
              {counts === null ? "Loading…" : `${String(counts.archivedClasses)} archived`}
            </span>
            <button type="button" className="text-dash-xs font-extrabold text-primary hover:underline flex items-center gap-1">
              <span>View all classes</span> <span>→</span>
            </button>
          </div>
        </div>


      </div>

      {/* 5. Middle Row Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Student Enrollment Trend */}
        <div className="lg:col-span-5 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Student Enrollment Trend</h3>
            <div className="flex items-center gap-1.5 rounded-xl border border-line bg-white px-2.5 py-1 text-dash-xs font-bold text-ink">
              <span className="text-muted font-bold text-[10px]">From:</span>
              <input
                type="date"
                defaultValue="2025-01-01"
                className="bg-transparent font-extrabold text-ink outline-none cursor-pointer text-[11px]"
              />
              <span className="text-muted font-bold text-[10px]">To:</span>
              <input
                type="date"
                defaultValue="2025-06-30"
                className="bg-transparent font-extrabold text-ink outline-none cursor-pointer text-[11px]"
              />
            </div>
          </div>

          <div className="relative h-44 w-full my-auto flex flex-col justify-end">
            <svg className="w-full h-36 overflow-visible" viewBox="0 0 500 150" preserveAspectRatio="none">
              <defs>
                <linearGradient id="purpleGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
                </linearGradient>
              </defs>
              <line x1="0" y1="0" x2="500" y2="0" stroke="#f1f5f9" strokeWidth="1" />
              <line x1="0" y1="50" x2="500" y2="50" stroke="#f1f5f9" strokeWidth="1" />
              <line x1="0" y1="100" x2="500" y2="100" stroke="#f1f5f9" strokeWidth="1" />
            </svg>

            <div className="flex justify-between text-[11px] font-bold text-muted mt-2">
              <span>Jan 2025</span>
              <span>Feb 2025</span>
              <span>Mar 2025</span>
              <span>Apr 2025</span>
              <span>May 2025</span>
              <span>Jun 2025</span>
            </div>
          </div>
        </div>

        {/* Users Overview */}
        <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <h3 className="font-dash text-dash-md font-extrabold text-ink mb-4">Users Overview</h3>

          <div className="flex flex-col items-center gap-4 my-auto">
            <div className="relative h-36 w-36 shrink-0 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#f1f5f9" strokeWidth="3.8" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                <span className="text-dash-md font-extrabold text-ink leading-none">—</span>
                <span className="text-[10px] font-bold text-muted mt-0.5 uppercase">Total Users</span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 w-full text-dash-xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-purple-500" />Students</span>
                <span className="font-bold text-muted">—</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Parents</span>
                <span className="font-bold text-muted">—</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />Teachers</span>
                <span className="font-bold text-muted">—</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-ink"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Others</span>
                <span className="font-bold text-muted">—</span>
              </div>
            </div>
          </div>
        </div>

        {/* Children by class */}
        <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h3 className="font-dash text-dash-md font-extrabold text-ink mb-1">Children by class</h3>
          {/*
            🔴 THIS PANEL SPLIT CHILDREN INTO **Primary (1–5) · Middle (6–8) ·
            High School (9–10) · Higher Secondary (11–12)** — four dashes under
            four bands this product does not have. Kalqy serves Nursery, LKG and
            UKG; `AgeBand` is `BAND_3_4 | BAND_4_5 | BAND_5_6` and there is no
            grade nine anywhere in the schema. Even filled in, every one of those
            rows would have read zero forever.

            ⚠️ THE REAL SPLIT IS PER CLASS, and `SchoolDetail.classes` has
            carried it — with its own `students` count — all along.
          */}
          <p className="text-dash-xs font-medium text-muted mb-3">
            Every class in this school, with the children enrolled in it.
          </p>
          <div className="my-auto flex flex-col gap-1.5 text-dash-xs">
            {detailResource.data === null ? (
              <span className="font-semibold text-muted">Loading…</span>
            ) : detailResource.data.classes.length === 0 ? (
              <span className="font-semibold text-muted">This school has no classes yet.</span>
            ) : (
              detailResource.data.classes.map((klass) => (
                <div key={klass.id} className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 font-bold text-ink">
                    <span className="truncate">{klass.name}</span>
                    {/* ⚠️ ARCHIVED CLASSES ARE LISTED AND MARKED, never dropped —
                        the same rule `SchoolClassDetail` states on the wire. */}
                    {klass.archivedAt === null ? null : (
                      <span className="shrink-0 rounded bg-canvas px-1.5 py-0.5 text-[10px] font-bold text-muted">
                        archived
                      </span>
                    )}
                  </span>
                  <span className="tabular shrink-0 font-bold text-muted">{klass.students}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 6. Bottom Row Grid (4 Cards Grid) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
        {/* Fee Collection Overview */}
        <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Fee Collection Overview</h3>
            <select className="rounded-xl border border-line bg-white px-2 py-1 text-dash-xs font-bold text-ink cursor-pointer">
              <option>This Month</option>
              <option>This Term</option>
            </select>
          </div>

          <div>
            <span className="text-dash-lg font-extrabold text-ink block">—</span>
            <span className="text-[11px] font-medium text-muted block mb-4">Not measured yet</span>
          </div>

          <div className="flex items-end justify-between gap-2 h-28 my-auto pt-2">
            {/* 🔴 A FEE-REVENUE BAR CHART — tuition, transport, exam and library fees — for a product that has no fees, no transport and no library. */}
            <p className="font-dash text-dash-xs font-semibold text-muted">
              The product does not collect fees, so there is nothing to break down.
            </p>
          </div>
        </div>

        {/* Attendance Overview */}
        <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col">
          <h3 className="font-dash text-dash-md font-extrabold text-ink mb-2">Attendance</h3>
          {/*
            🔴 THIS PANEL READ **Present 92.6% (2,275) · Absent 5.2% (128) ·
            Leave 2.2% (53)** FOR EVERY SCHOOL, whichever school was open. Three
            shares to one decimal place, two and a half thousand attendance
            marks, and a "Leave" category the product has no concept of.

            ⚠️ AND THE REASON IT CANNOT BE WIRED IS ALREADY WRITTEN DOWN, one
            tier away. `GET /parent/children/:playerId/attendance` says it in
            its own description: *"deliberately no attendance percentage:
            `Absence` records only absences, so the denominator would have to be
            invented."* This panel is what inventing it looks like.
          */}
          <p className="font-dash text-dash-xs font-semibold text-muted">
            The register records absences, not attendance — there is no roll-call
            denominator to divide by, so a school-wide percentage would have to be
            made up. A class register is where a teacher sees who was in.
          </p>
        </div>

        {/* Recent Activities */}
        <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Recent Activities</h3>
            <button type="button" className="text-dash-xs font-bold text-primary hover:underline cursor-pointer">
              View All
            </button>
          </div>

          <div className="space-y-3 text-dash-xs my-auto">
            {/* 🔴 FIVE INVENTED EVENTS, including a fee payment of Rs 15,600 by a child's parent. The platform has an activity feed endpoint, but it does not answer per-school and nothing here was reading it. */}
            <p className="font-dash text-dash-xs font-semibold text-muted">
              No activity feed is wired to this panel yet.
            </p>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-dash text-dash-md font-extrabold text-ink">Upcoming Events</h3>
            <button type="button" className="text-dash-xs font-bold text-primary hover:underline cursor-pointer">
              View Calendar
            </button>
          </div>

          <div className="space-y-3 text-dash-xs my-auto">
            {/* 🔴 FOUR INVENTED DIARY ENTRIES — a parent-teacher meeting, a sports day, a monthly examination — printed as though a school had scheduled them. The product has no calendar. */}
            <p className="font-dash text-dash-xs font-semibold text-muted">
              No calendar is wired to this panel yet.
            </p>
          </div>
        </div>
      </div>

      {/* 7. Platform Operator Safety & Admin Management Panel */}
      <div className="mt-8 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-6">
        <h3 className="text-dash-md font-extrabold text-ink border-b border-line pb-3">
          Operator Administration & Safety Controls
        </h3>

        {school.adminCount === 0 && (
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {t.noAdminConsequence}
          </Note>
        )}

        <SchoolFacts schoolId={school.id} lang={lang} auth={auth} refreshKey={changes} />

        <div className="pt-4 border-t border-line">
          <h4 className="text-dash-sm font-extrabold text-ink">{t.lifecycle}</h4>
          {isTerminal(school) ? (
            <p className="mt-2 max-w-prose text-muted">{t.terminal}</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {school.legalNextStatuses.map((to) => (
                <DashButton
                  key={to}
                  variant={to === 'ARCHIVED' ? 'destructive' : 'secondary'}
                  disabled={busy}
                  onClick={() => {
                    if (needsConfirmation(to)) setConfirming(to)
                    else void move(to)
                  }}
                >
                  {t.moveTo(STATUS_LABEL[to][lang])}
                </DashButton>
              ))}
            </div>
          )}
        </div>

        {confirming !== null && (
          <div className="mt-4 max-w-prose rounded-lg p-4 bg-rose-50 border border-rose-200 text-rose-900">
            <p className="font-bold">{t.archiveConfirm}</p>
            <p className="mt-1 text-dash-xs">{t.archiveMeaning}</p>
            <div className="mt-4 flex gap-2">
              <DashButton variant="destructive" disabled={busy} onClick={() => void move(confirming)}>
                {t.confirm}
              </DashButton>
              <DashButton variant="ghost" onClick={() => setConfirming(null)}>
                {t.cancel}
              </DashButton>
            </div>
          </div>
        )}

        {failure !== null && (
          <div className="mt-4 rounded-xl p-3 bg-rose-50 border border-rose-200 text-rose-800 font-bold text-dash-xs">
            Action failed: {failure}
          </div>
        )}

        <RegionPanel school={school} lang={lang} auth={auth} onChanged={changed} />
        <ProvisionAdminPanel school={school} lang={lang} auth={auth} onChanged={changed} />
        <ActAsPanel school={school} lang={lang} auth={auth} onActAs={onActAs} />
      </div>
    </div>
  )
}

/**
 * The rule between two panels inside the open school.
 *
 * ⚠️ FOUR FORMS IN ONE CARD NEEDED A DIVIDER AND HAD NONE. Region, provisioning
 * and support access were separated by margin alone, so an operator scrolling
 * read one continuous form and could not tell which button belonged to which
 * question — on a panel where one of the buttons is irreversible.
 */
function PanelRule() {
  return <hr className="mt-6 border-t border-line" />
}

/* ────────────────────────────────────── F2 · one school in full ── */

/** A labelled fact. Two columns, so a column of labels reads down the page. */
function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line py-2 last:border-b-0">
      <span className="min-w-[14rem] text-dash-xs font-bold text-muted">{label}</span>
      <span className="text-dash-sm text-ink">{children}</span>
    </div>
  )
}

/**
 * Everything the platform knows about one school.
 *
 * 🔴 IT IS ONE REQUEST, NOT SIX. `getSchoolDetail` exists precisely so that
 * clicking a table row does not open a panel with six independent spinners in
 * it — the alternative was `/schools/:id/staff`, `/features`, `/audit?targetId`
 * and three more, each with its own failure state.
 *
 * 🔴 AND IT IS FETCHED ONLY WHEN A SCHOOL IS ACTUALLY OPEN. The read is audited
 * server-side because it carries staff sign-in addresses; a prefetch on hover,
 * or a fetch for every row in the table, would write a `SUPER_ADMIN_ACCESS` row
 * per mouse movement into the table an incident is reconstructed from.
 * `useResource`'s key is the school id, so switching schools refetches and
 * closing the panel unmounts it.
 */
function SchoolFacts({
  schoolId,
  lang,
  auth,
  refreshKey,
}: {
  schoolId: string
  lang: Lang
  auth?: StaffAuth
  /** Bumped by the panel's writes. See `SchoolDetail`'s `changed`. */
  refreshKey: number
}) {
  const t = PLATFORM_COPY[lang]
  const resource = useResource<SchoolDetail>(
    (signal) => getSchoolDetail(schoolId, { ...auth, signal }),
    ['platform-school-detail', schoolId, refreshKey],
  )

  return (
    <div className="mt-6">
      {/* ⚠️ THE AUDIT SENTENCE IS BEFORE THE DATA, NOT UNDER IT. An operator who
          learns afterwards that the log names them is an operator who has
          already looked — X4's panel makes the identical argument. */}
      <div className="max-w-prose">
        <Note tone="neutral" icon={<PrivacyIcon size={16} />}>
          {t.detailAudited}
        </Note>
      </div>

      <div className="mt-4">
        <Surface resource={resource} lang={lang} skeletonRows={6} emptyMessage={t.detailLoadFailed}>
          {(detail) => (
            <>
              {/* ── the counts ── */}
              <StatGrid>
                <StatCard
                  label={t.countStudents}
                  value={detail.counts.students}
                  icon={<ChildrenIcon size={18} />}
                  tone="primary"
                />
                <StatCard
                  label={t.countActive}
                  value={detail.counts.activeStudents}
                  caption={t.activeWindow(detail.counts.engagementWindowDays)}
                  icon={<PlayIcon size={18} />}
                  tone="leaf"
                />
                <StatCard
                  label={t.countClasses}
                  value={detail.counts.classes}
                  icon={<SchoolIcon size={18} />}
                  tone="indigo"
                />
                <StatCard
                  label={t.countStaff}
                  value={
                    detail.counts.admins +
                    detail.counts.teachers +
                    detail.counts.coordinators +
                    detail.counts.principals
                  }
                  icon={<PointIcon size={18} />}
                  tone="teal"
                />
              </StatGrid>

              {/* ── the settings ── */}
              <SectionHead>{t.schoolSettingsTitle}</SectionHead>
              <Panel>
                <FactRow label={t.settingRecording}>
                  {detail.settings.recordingDefault ? t.yes : t.no}
                </FactRow>
                <FactRow label={t.settingLanguage}>{detail.settings.defaultLanguage}</FactRow>
                <FactRow label={t.settingYear}>
                  {detail.settings.academicYearLabel ?? t.settingUnset}
                </FactRow>
                <FactRow label={t.region}>{REGION_LABEL[detail.school.region]}</FactRow>
              </Panel>

              {/* ── who can sign in ── */}
              <SectionHead count={detail.staff.length}>{t.schoolPeople}</SectionHead>
              {detail.staff.length === 0 ? (
                <Note tone="warning" icon={<AlertIcon size={16} />}>
                  {t.peopleNone}
                </Note>
              ) : (
                <TableCard>
                  <table className="w-full border-collapse">
                    <Thead
                      headers={[t.adminName, t.peopleRole, t.peopleEmail, t.peopleLastSeen, '']}
                    />
                    <tbody>
                      {detail.staff.map((person) => (
                        <Tr key={person.userId}>
                          <Td bold>{person.name ?? person.userId}</Td>
                          <Td soft>{person.role}</Td>
                          {/* 🔴 THE ANSWER TO "which address do they sign in
                              with", which is the first question this page gets
                              asked. `null` is a real state — a parent row, or
                              staff created before the column was required. */}
                          <Td>
                            {person.email ?? (
                              <span className="text-muted">{t.peopleNoEmail}</span>
                            )}
                          </Td>
                          <Td soft>
                            {person.lastLoginAt === null
                              ? t.peopleNever
                              : new Date(person.lastLoginAt).toLocaleDateString()}
                          </Td>
                          <Td>
                            {/* ⚠️ Deactivated staff are LISTED and marked, not
                                hidden: "the head teacher left in March" is the
                                answer to half the calls that reach this page. */}
                            {person.isActive ? null : (
                              <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                                {t.peopleDeactivated}
                              </StatusPill>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </table>
                </TableCard>
              )}

              {/* ── the classes ── */}
              <SectionHead count={detail.classes.length}>{t.schoolClasses}</SectionHead>
              {detail.classes.length === 0 ? (
                <Note tone="info" icon={<InfoIcon size={16} />}>
                  {t.classesNone}
                </Note>
              ) : (
                <>
                  <TableCard>
                    <table className="w-full border-collapse">
                      <Thead
                        headers={[
                          t.schoolClasses,
                          t.classesTeacher,
                          t.classesChildren,
                          t.classesCode,
                          t.classesYear,
                          '',
                        ]}
                      />
                      <tbody>
                        {detail.classes.map((klass) => (
                          <Tr key={klass.id}>
                            <Td bold>
                              {klass.name}
                              {klass.grade === null && klass.section === null ? null : (
                                <span className="ml-2 text-dash-xs font-normal text-muted">
                                  {[klass.grade, klass.section].filter(Boolean).join(' ')}
                                </span>
                              )}
                            </Td>
                            <Td soft>
                              {klass.hasTeacher ? (
                                (klass.teacherName ?? '—')
                              ) : (
                                /* 🔴 A class with no teacher is a class nobody
                                   can run a session in. It is a finding, not a
                                   blank cell. */
                                <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
                                  {t.classesNoTeacher}
                                </StatusPill>
                              )}
                            </Td>
                            <Td num>{klass.students}</Td>
                            <Td>
                              {klass.code === null ? (
                                <span className="text-dash-xs text-muted">{t.classesNoCode}</span>
                              ) : (
                                <span className="tabular font-bold">{klass.code}</span>
                              )}
                            </Td>
                            <Td soft>{klass.academicYear ?? t.settingUnset}</Td>
                            <Td>
                              {klass.archivedAt === null ? null : (
                                <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                                  {t.classesArchived}
                                </StatusPill>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </tbody>
                    </table>
                  </TableCard>
                  {/* ⚠️ NO SILENT INCLUSION EITHER. The archived rows are in the
                      table above and every count on this page excludes them —
                      saying so is what stops the two being read as one number. */}
                  {detail.counts.archivedClasses > 0 && (
                    <p className="mt-3 text-dash-xs text-muted">
                      {t.archivedClasses(detail.counts.archivedClasses)}
                    </p>
                  )}
                </>
              )}

              {/* ── X1's flags, read-only here ── */}
              <SectionHead count={detail.features.length}>{t.schoolFeatures}</SectionHead>
              {detail.features.length === 0 ? (
                <p className="text-muted">{t.featuresNone}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {detail.features.map((flag) => (
                    <li key={flag.feature}>
                      <StatusPill
                        tone={flag.enabled ? 'success' : 'neutral'}
                        icon={flag.enabled ? <CheckIcon size={14} /> : <InfoIcon size={14} />}
                      >
                        {flag.feature}
                      </StatusPill>
                    </li>
                  ))}
                </ul>
              )}

              {/* ── S1's rollovers ── */}
              <SectionHead count={detail.rollovers.length}>{t.schoolRollovers}</SectionHead>
              {detail.rollovers.length === 0 ? (
                <p className="text-muted">{t.rolloversNone}</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {detail.rollovers.map((run) => (
                    <li key={run.id} className="flex flex-wrap items-baseline gap-3">
                      <span className="font-bold text-ink">
                        {t.rolloverRow(run.fromYear, run.toYear)}
                      </span>
                      <span className="text-dash-xs text-muted">
                        {run.completedAt === null
                          ? t.rolloverIncomplete
                          : new Date(run.completedAt).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* ── what has been done to it ── */}
              <SectionHead count={detail.recentActivity.length}>{t.schoolHistory}</SectionHead>
              <p className="mb-3 max-w-prose text-dash-xs text-muted">{t.historyNote}</p>
              {detail.recentActivity.length === 0 ? (
                <p className="text-muted">{t.historyNone}</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {detail.recentActivity.map((row) => (
                    <li key={row.id} className="rounded-lg bg-canvas px-4 py-3">
                      <p className="text-dash-sm text-ink">{row.summary}</p>
                      {/* 🔴 A NULL ACTOR IS THE SYSTEM AND IS NOT WRITTEN AS A
                          NAME — `actorLabel`'s rule, applied to a row that is
                          not an `AuditRow` (it carries no target: every row here
                          targets this school by construction). */}
                      <p className="mt-1 text-dash-xs text-muted">
                        {new Date(row.createdAt).toLocaleString()} {'·'}{' '}
                        {row.actorId === null
                          ? t.system
                          : row.actorLabel.length > 0
                            ? row.actorLabel
                            : row.actorId}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Surface>
      </div>
    </div>
  )
}

/**
 * X2 — the retention jurisdiction.
 *
 * 🔴 THE RESPONSE'S ONE EXTRA NUMBER IS THE POINT OF THIS PANEL. `retainUntil`
 * is stamped at grant time and never recomputed, so changing the region changes
 * what NEW consents are stamped with and nothing already written. An operator
 * who changes it to "fix retention" will assume otherwise — and silence there
 * produces a compliance claim nobody checked.
 */
function RegionPanel({
  school,
  lang,
  auth,
  onChanged,
}: {
  school: SchoolSummary
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [busy, setBusy] = useState(false)
  const [kept, setKept] = useState<number | null>(null)
  const [failure, setFailure] = useState<WriteFailure | null>(null)

  /* An archived school is read-only, and the server refuses this outright. */
  if (school.status === 'ARCHIVED') return null

  const change = async (region: Region) => {
    if (region === school.region) return
    setBusy(true)
    setFailure(null)
    try {
      const result = await changeSchoolRegion(school.id, region, auth)
      setKept(result.consentsKeepingTheirRetainUntil)
      onChanged()
    } catch (err) {
      setFailure(classifyWriteError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PanelRule />
      <div className="mt-4">
        <h3 className="mb-3 text-dash-sm font-extrabold text-ink">{t.changeRegion}</h3>
        <Field label={t.addSchoolRegion}>
          <Select
            value={school.region}
            disabled={busy}
            onChange={(e) => void change(e.target.value as Region)}
            aria-label={t.changeRegion}
          >
            {REGIONS_BY_NAME.map((r) => (
              <option key={r} value={r}>
                {REGION_LABEL[r]}
              </option>
            ))}
          </Select>
        </Field>

        {/* 🔴 The sentence an operator who thinks this "fixes retention" must
            read. It is a block rather than a pill because it is two clauses
            long and a 700px lozenge is not a sentence. */}
        {kept !== null && (
          <div className="mt-4">
            <Note tone="neutral" icon={<PrivacyIcon size={16} />}>
              {t.regionKept(kept)}
            </Note>
          </div>
        )}
        {failure !== null && (
          <div className="mt-4">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failureText(failure, lang, t.nameTaken)}
            </Note>
          </div>
        )}
      </div>
    </>
  )
}

/**
 * 🔴 THE GATE — *"a school cannot mint its own first admin"* (admin-panel.md §3).
 *
 * ⚠️ AND THE ONE PANEL IN THIS PRODUCT THAT RENDERS A LIVE CREDENTIAL. There is
 * no email sender and no approved WhatsApp invite template, so the response body
 * is the only channel that exists — the operator's screen is genuinely the last
 * place this password can be read. The panel says so in three sentences rather
 * than one, because an operator who closes it expecting to find it later has
 * locked a school out of its own account on the day it onboarded.
 */
function ProvisionAdminPanel({
  school,
  lang,
  auth,
  onChanged,
}: {
  school: SchoolSummary
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)
  const [issued, setIssued] = useState<ProvisionedAdmin | null>(null)

  /* ⚠️ BEFORE THE EARLY RETURN. Hooks cannot run conditionally, and the archived
     branch below is a `return null` — evaluating this after it is the classic
     "rendered fewer hooks than expected" crash the first time a school is
     archived while its panel is open. */
  const pw = useMemo(
    () => passwordState(password, confirm, { email: email.trim(), schoolName: school.name }),
    [password, confirm, email, school.name],
  )

  if (school.status === 'ARCHIVED') return null

  const submit = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const admin = await provisionSchoolAdmin(
        school.id,
        {
          name: name.trim(),
          email: email.trim(),
          /* 🔴 REQUIRED. The submit button is disabled unless `pw.kind` is
             'ready', so this branch is the only one that can reach the server —
             the fallback exists because TypeScript cannot see that guard, not
             because an empty password is a case worth sending. */
          password: pw.kind === 'ready' ? pw.password : '',
          /* Advisory — the server re-checks the policy against the row it holds.
             Sent so `ECHOES_IDENTITY` can be refused on the way in rather than
             after the account exists. */
          schoolName: school.name,
        },
        auth,
      )
      setIssued(admin)
      setName('')
      setEmail('')
      setPassword('')
      setConfirm('')
      onChanged()
    } catch (err) {
      setFailure(classifyWriteError(err))
    } finally {
      setBusy(false)
    }
  }

  /*
   * 🔴 THE FORM IS REPLACED BY THE CREDENTIAL, NOT SHOWN BESIDE IT. A visible
   * form under a live password is an invitation to create a second admin while
   * the first one's credential is still on screen and unhanded-over.
   */
  if (issued !== null) {
    return (
      <>
        <PanelRule />
        {/* ⚠️ NO RE-SEND BUTTON REACHES THIS PANEL IN PRACTICE, AND THE PROP IS
            PASSED ANYWAY. `provisionSchoolAdmin` is the operator-password route,
            so `passwordSource` is always `'OPERATOR'` here and `canResendInvite`
            refuses — but the card must not depend on its caller knowing that. */}
        <CredentialCard
          admin={issued}
          schoolId={school.id}
          lang={lang}
          auth={auth}
          onDone={() => setIssued(null)}
        />
      </>
    )
  }

  return (
    <>
      <PanelRule />
      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <h3 className="text-dash-sm font-extrabold text-ink">{t.provisionTitle}</h3>
        <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.provisionHint}</p>

        <div className="mt-4 grid max-w-xl gap-4 sm:grid-cols-2">
          <Field label={t.adminName} htmlFor="admin-name" width="full">
            <Input
              id="admin-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </Field>

          <Field label={t.adminEmail} htmlFor="admin-email" width="full">
            <Input
              id="admin-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
            />
          </Field>
        </div>

        {/* 🔴 THE PASSWORD FIELD THIS PANEL ONCE REFUSED TO HAVE, AND IT IS NOW
            THE ONLY WAY IN. It sat behind a chooser between an invitation link
            and a password; the link went (`school.dto.ts`) and so did the
            chooser, because a choice between one option is a control that
            cannot change anything. `passwordState` and the server both refuse
            the `Welcome@123` family. */}
        <PasswordFields
          idPrefix={`provision-${school.id}`}
          lang={lang}
          password={password}
          confirm={confirm}
          state={pw}
          disabled={busy}
          onPassword={setPassword}
          onConfirm={setConfirm}
        />

        <div className="mt-6">
          <DashButton
            type="submit"
            disabled={
              busy ||
              name.trim().length === 0 ||
              email.trim().length === 0 ||
              /* ⚠️ `!== 'ready'` RATHER THAN `=== 'problem'`. The empty form is
                 no longer a valid submission — there is nothing to generate — so
                 an untouched password box must keep the button disabled rather
                 than sending a 400 on a field that looked optional. */
              pw.kind !== 'ready'
            }
          >
            {busy ? t.provisioning : t.provision}
          </DashButton>
        </div>

        {failure !== null && (
          <div className="mt-4">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failureText(failure, lang, t.emailTaken)}
            </Note>
          </div>
        )}
      </form>
    </>
  )
}

/* ─────────────────────────────────────────────────────────── X4 ── */

/**
 * X4 — support access, on the school it is access TO.
 *
 * 🔴 IT LIVES HERE RATHER THAN ON ITS OWN SCREEN BECAUSE THE QUESTION IS
 * ALWAYS ABOUT A SCHOOL. `admin-panel.md` §8 frames the feature as *"when
 * support needs to see what a school sees"* — an operator arrives having been
 * told "the head at Little Stars cannot open reports", and the school is the
 * thing they already have in front of them. A separate screen would ask them to
 * find it again, by an id they do not have.
 *
 * 🔴 THE STAFF LIST IS FETCHED ONLY WHEN A SCHOOL IS OPEN, AND NEVER FOR ALL
 * SCHOOLS AT ONCE. A cross-tenant directory of every staff member on the
 * platform is not a thing this screen should be able to render, and the surest
 * way to keep it that way is for no call to exist that could.
 */
function ActAsPanel({
  school,
  lang,
  auth,
  onActAs,
}: {
  school: SchoolSummary
  lang: Lang
  auth?: StaffAuth
  onActAs?: (userId: string, reason: string) => Promise<void>
}) {
  const t = PLATFORM_COPY[lang]
  const [userId, setUserId] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)

  const staff = useResource<SchoolStaff[]>(
    (signal) => getSchoolStaff(school.id, { ...auth, signal }),
    ['platform-school-staff', school.id],
    { enabled: onActAs !== undefined, isEmpty: (rows) => rows.length === 0 },
  )

  const start = useCallback(async () => {
    if (!onActAs || userId.length === 0 || !reasonIsUsable(reason)) return
    setBusy(true)
    setFailure(null)
    try {
      await onActAs(userId, reason.trim())
      /*
       * ⚠️ NOTHING IS RESET ON SUCCESS, AND NOTHING NEEDS TO BE. The router
       * navigates into the impersonated tier the moment this resolves, so this
       * component unmounts — clearing the form first would be state written to
       * a screen nobody sees again.
       */
    } catch (err) {
      setFailure(classifyWriteError(err))
      setBusy(false)
    }
  }, [onActAs, userId, reason])

  /* D4 — no control at all rather than a control that cannot work. */
  if (!onActAs) return null

  /*
   * ⚠️ AN ARCHIVED SCHOOL IS READ-ONLY EVERYWHERE ELSE ON THIS SCREEN, AND
   * ACTING AS ITS STAFF IS NOT AN EXCEPTION. Support access exists to reproduce
   * what a working customer sees; a school nobody can sign into has nothing to
   * reproduce, and the impersonation would be an audited read of a closed
   * school's data with no support call behind it.
   */
  if (school.status === 'ARCHIVED') return null

  const reasonReady = reasonIsUsable(reason)

  return (
    <>
      <PanelRule />
      <div className="mt-4">
        <h3 className="text-dash-sm font-extrabold text-ink">{t.actAsTitle}</h3>
        {/* 🔴 THE RECORD AND THE TIME-BOX, BEFORE THE FORM. §8: never a silent
            view-as — an operator who learns afterwards that the log names them
            is an operator who has already acted. */}
        <div className="mt-3 max-w-prose">
          <Note tone="neutral" icon={<PrivacyIcon size={16} />}>
            {t.actAsHint}
          </Note>
        </div>

        <div className="mt-4">
          <Surface resource={staff} lang={lang} emptyMessage={t.actAsNoStaff} skeletonRows={2}>
            {(rows) => (
              <>
                <div className="grid max-w-xl gap-4 sm:grid-cols-2">
                  <Field
                    label={t.actAsPickStaff}
                    htmlFor={`act-as-who-${school.id}`}
                    width="full"
                  >
                    <Select
                      id={`act-as-who-${school.id}`}
                      value={userId}
                      disabled={busy}
                      onChange={(e) => setUserId(e.target.value)}
                    >
                      <option value="">{t.actAsPickStaffNone}</option>
                      {rows.map((person) => (
                        <option key={person.userId} value={person.userId}>
                          {person.label} {'·'} {person.role}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field
                    label={t.actAsReason}
                    htmlFor={`act-as-why-${school.id}`}
                    hint={t.actAsReasonHint}
                    width="full"
                  >
                    <Input
                      id={`act-as-why-${school.id}`}
                      value={reason}
                      maxLength={500}
                      disabled={busy}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </Field>
                </div>

                {/* 🔴 THE FLOOR IS STATED WHILE THE BOX IS SHORT, not after a
                    rejected submit. `impersonateSchema` refuses under eight
                    characters, and a 400 arriving after the click reads as the
                    form being broken rather than as a requirement. */}
                {reason.trim().length > 0 && !reasonReady && (
                  <p className="mt-3">
                    <StatusPill tone="warning" icon={<InfoIcon size={14} />}>
                      {t.actAsReasonTooShort}
                    </StatusPill>
                  </p>
                )}

                <div className="mt-4">
                  <DashButton
                    variant="secondary"
                    disabled={busy || userId.length === 0 || !reasonReady}
                    onClick={() => void start()}
                  >
                    <PrivacyIcon size={16} />
                    {busy ? t.actAsStarting : t.actAsStart}
                  </DashButton>
                </div>
              </>
            )}
          </Surface>
        </div>

        {failure !== null && (
          <div className="mt-4">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failureText(failure, lang, t.actAsFailed)}
            </Note>
          </div>
        )}
      </div>
    </>
  )
}
